import { Component, OnDestroy, OnInit, signal, computed, inject } from '@angular/core';
import { Shipment } from '../models/shipment';
import { SessionStorageService } from '../../services/session-storage.service';
import { VehicleType } from '../../fleet/vehicle/params/vehicle-type';
import { PayloadType } from '../../models/meta-params/payload-type';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PayloadItemView } from '../../models/meta-params/payload-item-view';
import { VehicleConfig } from '../../fleet/vehicle/params/vehicle-config';
import { VehicleCapacity } from '../../fleet/vehicle/params/vehicle-capacity';
import { QuantityView } from '../../models/meta-params/quantity-view';
import { Company } from '../../company/company.model';
import { MenuItem, MessageService } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '../../location/location.model';
import { LocationService } from "../../location/location.service";
import { Customer } from "../../customer/customer.model";
import { ContractsService } from "../../contracts/contracts.service";
import { Contracts } from "../../contracts/models/contracts.model";
import { DeliveryRateCard } from "../../contracts/models/delivery-rate-card.model";
import { ContractPricingDecisionRequest } from "../../contracts/models/contract-pricing-decision.model";
import { CustomerService } from "../../customer/customer.service";
import { CompanyService } from "../../company/company.service";
import { ShipmentService } from "../shipment.service";
import { ShipmentIntakeService } from "../shipment-intake.service";
import { BookingOption } from "../models/shipment-booking-option.enum";
import { ShipmentType } from "../models/shipment-type.enum";
import {
    ShipmentIntakePricing,
    ShipmentIntakeReadiness,
    ShipmentIntakeRequest,
    ShipmentIntakeResponse,
    ShipmentIntakeRoutePreview,
    ShipmentIntakeWarehouseItemOption,
    ShipmentIntakeLaborRequirement
} from "../models/intake/shipment-intake.model";
import { FieldWorkService } from '../../field-work/field-work.service';
import { FieldLaborRoleOption } from '../../field-work/field-work.models';
import { CreateLocationComponent } from 'src/app/location/create-location/create-location.component';
import { DialogService } from 'primeng/dynamicdialog';
import { EnterpriseFeatureService } from 'src/app/services/enterprise-features.service';
import { PartyService } from 'src/app/party/party.service';
import { PartyType } from 'src/app/party/party-type.enum';
import { CommonModule } from '@angular/common';
import { ToastModule } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { CustomerFormComponent } from '../../customer/customer-form/customer-form.component';
import { CompanyFormComponent } from '../../company/company-form/company-form.component';
import { DatePickerModule } from 'primeng/datepicker';
import { ProgressBarModule } from "primeng/progressbar";
import { FieldsetModule } from "primeng/fieldset";
import { InputGroupModule } from "primeng/inputgroup";
import { InputGroupAddonModule } from "primeng/inputgroupaddon";
import { IftaLabelModule } from "primeng/iftalabel";
import { PermissionService } from 'src/app/security/permission.service';
import { app_permissions } from 'src/app/security/app-permissions';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageFormSectionComponent } from '../../shared/page-layout/page-form-section.component';
import { ShipmentWaypoint } from '../models/waypoint/shipment.waypoint';
import { ShipmentWaypointType } from '../models/waypoint/shipment-waypoint-type';
import { LogisticsProjectLookupComponent } from '../../logistics-projects/logistics-project-lookup.component';
import { LogisticsProjectsService } from '../../logistics-projects/logistics-projects.service';
import { CrmClient } from '../../crm/crm.model';
import { CrmService } from '../../crm/crm.service';
import { OrdersService } from '../../order/order.service';
import { PaymentTerm } from '../../order/models/payment-term.model';
import { resolveSisterCompanyScope, SisterCompanyScope } from '../../shared/utils/sister-company-scope.util';
import {
    GoogleCloudStorageService,
    ShipmentUploadProgress
} from 'src/app/services/google/google-cloud-storage.service';
import { ShipmentDeliveryAttachment } from '../models/shipment-delivery.attachment';
import { CommercialPartyPickerComponent } from '../../shared/commercial-party-picker/commercial-party-picker.component';
import { CommercialPartyReference } from '../../shared/commercial-party-picker/commercial-party.model';
import { WarehousingService } from '../../warehousing/warehousing.service';
import { WarehousingMasterData } from '../../warehousing/warehousing.model';
import { ContainerTrackingService } from '../../container-tracking/container-tracking.service';
import { ContainerJourneyTemplateView } from '../../container-tracking/models/container-journey-plan.model';
import { warehousePickupIssue } from '../warehouse-pickup';

interface RouteStopDraft {
    waypointId?: number;
    location?: Location;
    contact?: Customer;
    stopType: string;
    scheduledArrival?: Date | null;
    scheduledDeparture?: Date | null;
    remarks?: string;
}

interface ShipmentDocumentDraft {
    file: File;
    progress: number;
    state: string;
}

interface PartyCustomerLoadingOption {
    id: number;
    name: string;
    partyCustomerLoading: true;
}

type PartyCustomerOption = Customer | PartyCustomerLoadingOption;

interface LocationLoadingOption {
    id: number;
    name: string;
    locationLoading: true;
}

type ShipmentLocationOption = Location | LocationLoadingOption;

@Component({
    selector: 'app-create-shipment',
    templateUrl: './create-shipment.component.html',
    styleUrls: ['./create-shipment.component.css'],
    standalone: true,
    imports: [
        LogisticsProjectLookupComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        ToastModule,
        BreadcrumbModule,
        SelectModule,
        SelectButtonModule,
        ButtonModule,
        InputNumberModule,
        DatePickerModule,
        InputTextModule,
        TextareaModule,
        CustomerFormComponent,
        CompanyFormComponent,
        ProgressBarModule,
        FieldsetModule,
        InputGroupModule,
        InputGroupAddonModule,
        IftaLabelModule,
        PageShellComponent,
        PageHeroComponent,
        PageFormSectionComponent,
        CommercialPartyPickerComponent
    ],
    providers: [
        MessageService,
        LocationService,
        ContractsService,
        CustomerService,
        CompanyService,
        ShipmentService,
        ShipmentIntakeService,
        DialogService,
        PartyService
    ]
})
export class CreateShipmentComponent implements OnInit, OnDestroy {

    readonly helpChecklist = [
        'Choose the bill-to commercial party and the pickup and drop-off locations.',
        'Set the pickup window, route type, and service category; add intermediate stops if needed.',
        'Add cargo lines with quantities and weight, then enter the requested truck count.',
        'Set the customer selling price and payment term if Commercials is available to you.',
        'Check Review & Create for missing details, then create the shipment.'
    ];

    commercialPartyReference: CommercialPartyReference = { source: 'CRM' };

    createShipmentForm: FormGroup;
    sourceLocationDropdown: FormControl;
    destinationLocationDropdown: FormControl;
    vehicleTypeDropdown: FormControl;
    vehicleConfigDropdown: FormControl;
    vehicleCapacityDropdown: FormControl;
    payloadTypeDropdown: FormControl;
    pickupDateSelect: FormControl;
    quantityRequiredInput: FormControl;
    uomQuantityInput: FormControl;
    itemWeightInput: FormControl;
    buyingPriceInput: FormControl;
    sellingPriceInput: FormControl;
    routeTypeDropdown: FormControl;
    serviceCategoryDropdown: FormControl;

    shipment: Shipment = new Shipment();
    /**
     * Location selectors render a sparse, virtualized page. Selected and
     * newly-created locations are pinned so their labels stay available when
     * they sit outside the current search result page.
     */
    private locationPins = signal<Location[]>([]);
    private locationPage = signal<ShipmentLocationOption[]>([]);
    locationOptions = computed(() => {
        const pinned = this.locationPins();
        const pinnedIds = new Set(pinned.map(location => location.id));
        const page = this.locationPage();
        return [...pinned, ...page.map((location, index) =>
            !this.isLocationLoadingOption(location) && !pinnedIds.has(location.id)
                ? location
                : this.locationLoadingOption(index)
        )];
    });
    locationOptionsLoading = false;
    locationSearchText = '';
    private readonly locationPageSize = 25;
    private locationQueryVersion = 0;
    private locationHasMore = true;
    private locationLoadedPages = new Set<number>();
    private locationLoadingPages = new Set<string>();
    private locationFilterTimer?: ReturnType<typeof setTimeout>;
    vehicleTypes: VehicleType[] = [];
    vehicleConfigurations: VehicleConfig[] = [];
    vehicleCapacities: VehicleCapacity[] = [];
    payloadTypes: PayloadType[] = [];
    uomList: any[] = [];
    weightUnitList: any[] = [];
    lengthUnitList: any[] = [];
    warehouseItemOptions: ShipmentIntakeWarehouseItemOption[] = [];
    private warehouseUomCache = new WeakMap<ShipmentIntakeWarehouseItemOption, any[]>();
    warehouseOptions: WarehousingMasterData[] = [];
    conditionOptions: WarehousingMasterData[] = [];
    warehouseItemsLoading = false;
    warehouseItemsMessage = '';
    readonly warehouseFlowOptions = [
        { label: 'Outbound from warehouse', value: 'OUTBOUND' },
        { label: 'Inbound to warehouse', value: 'INBOUND' }
    ];
    hasWarehouseItems(): boolean {
        return this.shipment.payloadItemList?.some(line => this.isWarehouseItemLine(line)) ?? false;
    }
    outboundPickupIssue(line: PayloadItemView): string {
        if (!this.isWarehouseItemLine(line) || !line.inventoryItemId || this.shipment.warehouseFlowDirection === 'INBOUND') return '';
        return warehousePickupIssue(line.warehouseItemName || line.warehouseItemNumber || `Item ${line.inventoryItemId}`,
            line.sourceWarehouseId, this.shipment.sourceLocation?.id, this.warehouseOptions);
    }
    onWarehouseFlowChange(): void {
        this.shipment.payloadItemList?.filter(line => this.isWarehouseItemLine(line)).forEach(line => {
            line.sourceWarehouseId = this.shipment.warehouseFlowDirection === 'INBOUND'
                ? undefined : this.selectedWarehouseItem(line)?.defaultWarehouseId;
        });
        this.refreshIntakePreviews();
    }
    inboundDestinationWarehouse(): WarehousingMasterData | undefined {
        const matches = this.warehouseOptions.filter(warehouse => warehouse.locationId === this.shipment.destinationLocation?.id
            && warehouse.warehouseOwnershipType === 'COMPANY' && warehouse.isActive !== false);
        return matches.length === 1 ? matches[0] : undefined;
    }
    readonly cargoLineSourceOptions = [
        { label: 'Warehouse Item', value: 'WAREHOUSE_ITEM', icon: 'fa-solid fa-warehouse' },
        { label: 'General Cargo', value: 'GENERAL_CARGO', icon: 'fa-solid fa-box' },
        { label: 'Container', value: 'CONTAINER', icon: 'fa-solid fa-boxes-packing' }
    ];
    readonly containerEquipmentTypeOptions = [
        { label: '20GP - 20 ft General Purpose', value: '20GP' },
        { label: '40GP - 40 ft General Purpose', value: '40GP' },
        { label: '40HC - 40 ft High Cube', value: '40HC' },
        { label: '45HC - 45 ft High Cube', value: '45HC' },
        { label: '20RF - 20 ft Refrigerated', value: '20RF' },
        { label: '40RF - 40 ft Refrigerated', value: '40RF' },
        { label: 'Tank', value: 'TANK' },
        { label: 'Flat Rack', value: 'FLAT_RACK' },
        { label: 'Open Top', value: 'OPEN_TOP' }
    ];
    companyList = signal<Company[]>([]);
    /**
     * Operational contacts come from the tenant Party Customer directory, not
     * from a location or CRM client's default customer. Results are paged and
     * fetched only when a contact picker is opened or searched.
     */
    private partyCustomerPins = signal<Customer[]>([]);
    private partyCustomerPage = signal<PartyCustomerOption[]>([]);
    partyCustomerOptions = computed(() => {
        const pinned = this.partyCustomerPins();
        const pinnedIds = new Set(pinned.map(customer => customer.id));
        const page = this.partyCustomerPage();
        return [...pinned, ...page.map((customer, index) =>
            !this.isPartyCustomerLoadingOption(customer) && !pinnedIds.has(customer.id)
                ? customer
                : this.partyCustomerLoadingOption(index)
        )];
    });
    partyCustomerOptionsLoading = false;
    partyCustomerSearchText = '';
    private readonly partyCustomerPageSize = 50;
    private partyCustomerQueryVersion = 0;
    private partyCustomerLoadedPages = new Set<number>();
    private partyCustomerLoadingPages = new Set<string>();
    private partyCustomerFilterTimer?: ReturnType<typeof setTimeout>;

    shipmentCreationInProgress = false;
    private continueToDeliveryAssignment = false;
    currencyValue = null
    quantityRequired: any = null;
    totalWeight: number = 0;
    cargoTotalWeights: number[] = [0];
    pickupDate: Date | null = null;
    deliveryWindowEnd: Date | null = null;
    searchPlaceResults: string[];
    intakeReadiness?: ShipmentIntakeReadiness;
    routePreview?: ShipmentIntakeRoutePreview;
    pricingPreview?: ShipmentIntakePricing;
    pricingWarnings: string[] = [];
    submissionIssues: string[] = [];
	paymentTerms: PaymentTerm[] = [];
	paymentTermId?: number;
    previewInProgress = false;
    selectedSenderCompany?: Company;
    selectedSenderContact?: Customer;
    selectedReceiverCompany?: Company;
    selectedReceiverContact?: Customer;
    selectedWatcher?: Customer;
    watcherList: Customer[] = [];
    shipmentReference = '';
    shipmentNotes = '';
    priority = 'NORMAL';
    currencyCode = 'SAR';
    rateSource = 'RATE_CARD';
    private rateMatchKey = '';
    private rateMatchSequence = 0;
    private pricingPreviewSequence = 0;
    buyingCostSource = 'OWNED';
    manualOverrideReason = '';
    pricingContractId?: number;
    pricingContractRevisionId?: number;
    pricingRateCardId?: number;
    selectedPricingContract?: Contracts;
    selectedPricingRateCard?: DeliveryRateCard & { displayLabel?: string };
    selectedSisterCompany?: Company;
    sisterCompanyScope: SisterCompanyScope | null = null;
    supplierClients = signal<Array<CrmClient & { displayLabel?: string }>>([]);
    selectedSupplierClient?: CrmClient & { displayLabel?: string };
    selectedSupplierContract?: Contracts;
    selectedSupplierRateCard?: DeliveryRateCard & { displayLabel?: string };
    contractOptions: Contracts[] = [];
    rateCardOptions: Array<DeliveryRateCard & { displayLabel?: string }> = [];
    supplierContractOptions: Contracts[] = [];
    supplierRateCardOptions: Array<DeliveryRateCard & { displayLabel?: string }> = [];
    contractOptionsLoading = false;
    rateCardOptionsLoading = false;
    supplierClientsLoading = false;
    supplierContractOptionsLoading = false;
    supplierRateCardOptionsLoading = false;
    routeStops: RouteStopDraft[] = [];
    shipmentDocumentQueue: ShipmentDocumentDraft[] = [];
    buyingPriceEditedManually = false;
    lastRecommendedBuyingPrice?: number;

    public routeTypes = [
        { label: 'Domestic', value: 'DOMESTIC' },
        { label: 'Cross-Border', value: 'CROSS_BORDER' }
    ];

    public serviceCategories = [
        { label: 'FOB', value: 'FOB' },
        { label: 'Door-to-Door', value: 'DOOR_TO_DOOR' },
        { label: 'Documentation', value: 'DOCUMENTATION' },
        { label: 'Full Service', value: 'FULL_SERVICE' },
        { label: 'LCL — Less than Container Load', value: 'LCL' },
        { label: 'FCL — Full Container Load', value: 'FCL' },
        { label: 'LTL — Less than Truckload', value: 'LTL' },
        { label: 'FTL — Full Truckload', value: 'FTL' }
    ];
    public priorityOptions = [
        { label: 'Normal', value: 'NORMAL' },
        { label: 'High', value: 'HIGH' },
        { label: 'Critical', value: 'CRITICAL' }
    ];
    public currencyOptions = [
        { label: 'SAR', value: 'SAR' },
        { label: 'USD', value: 'USD' },
        { label: 'AED', value: 'AED' }
    ];
    public buyingCostSourceOptions = [
        { label: 'Own Fleet', value: 'OWNED' },
        { label: 'Sister Company Fleet', value: 'SISTER_OWNED' },
        { label: 'Freelance Supplier', value: 'FREELANCE' },
        { label: 'Rental Supplier', value: 'RENTAL' },
        { label: 'Manual', value: 'MANUAL' }
    ];
    public rateSourceOptions = [
        { label: 'Rate Card', value: 'RATE_CARD' },
        { label: 'Manual', value: 'MANUAL' }
    ];
    public stopTypes = [
        { label: 'Secondary Pickup', value: ShipmentWaypointType.PICKUP },
        { label: 'Secondary Dropoff', value: ShipmentWaypointType.DROPOFF },
        { label: 'Cross Dock', value: ShipmentWaypointType.CROSS_DOCK },
        { label: 'Rest Area', value: ShipmentWaypointType.REST_AREA }
    ];

    breadcrumbItems: MenuItem[];
    homeBreadcrumb: MenuItem;

    companyFormHeader: string;
    companyFormSubmitLabel: string;
    selectedCustomer: Customer = new Customer();
    selectedCompany: Company = new Company();
    showCompanyForm: boolean = false;
    customerFormHeader: string;
    customerFormSubmitLabel: string;
    showCustomerForm: boolean = false;
    contactCreationTarget?: 'sender' | 'receiver' | 'stop';
    contactCreationStopIndex?: number;
    private sessionStorageService = inject(SessionStorageService);
    private contractService = inject(ContractsService);
    private companyService = inject(CompanyService);
    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private dialogService = inject(DialogService);
    private shipmentService = inject(ShipmentService);
    private shipmentIntakeService = inject(ShipmentIntakeService);
    private customerService = inject(CustomerService);
    private messageService = inject(MessageService);
    private locationService = inject(LocationService);
    private enterpriseFeaturesService = inject(EnterpriseFeatureService);
    private partyService = inject(PartyService);
    private permissionService = inject(PermissionService);
    private googleCloudStorageService = inject(GoogleCloudStorageService);
    private logisticsProjectsService = inject(LogisticsProjectsService);
    private crmService = inject(CrmService);
	private ordersService = inject(OrdersService);
    private warehousingService = inject(WarehousingService);
    private containerTrackingService = inject(ContainerTrackingService);
    private fieldWorkService = inject(FieldWorkService);
    private warehouseItemLoadSequence = 0;
    containerJourneyTemplates: ContainerJourneyTemplateView[] = [];
    containerJourneyTemplatesLoading = false;
    workforceRoles: FieldLaborRoleOption[] = [];
    workforceRequirements: ShipmentIntakeLaborRequirement[] = [];
    readonly workforceBillingUnits = ['HOUR', 'DAY', 'TASK', 'FLAT'];

    isEnabledEnterpriseFeatures = this.enterpriseFeaturesService.isShipmentsActive;
    isLoading: any;
    canSaveShipment: boolean = false;

    constructor() {
        this.vehicleTypes = this.sessionStorageService.getVehicleTypes()!!;
        this.payloadTypes = this.sessionStorageService.getPayloadTypes()!!;
        this.uomList = this.sessionStorageService.getUOMList() ?? [];
        this.weightUnitList = this.sessionStorageService.getWeightUnits() ?? [];
        this.lengthUnitList = this.sessionStorageService.getLengthUnits() ?? [];
        this.shipment.payloadItemList.push(this.createCargoLine());
        this.shipment.countryOfOrder = this.sessionStorageService.getCountryList()?.find(country => country.countryCode == 'SA')!!;
    }

    ngOnInit(): void {
        this.createShipmentForm = new FormGroup({
            companyDropdown: new FormControl(''),
            customerDropdown: new FormControl(''),
            sourceLocationDropdown: new FormControl('', Validators.required),
            destinationLocationDropdown: new FormControl('', Validators.required),
            pickupDateSelect: new FormControl('', Validators.required),
            vehicleTypeDropdown: new FormControl(''),
            vehicleConfigDropdown: new FormControl(''),
            vehicleCapacityDropdown: new FormControl(''),
            quantityRequiredInput: new FormControl('', Validators.required),
            payloadTypeDropdown: new FormControl('', Validators.required),
            uomQuantityInput: new FormControl('', Validators.required),
            itemWeightInput: new FormControl('', Validators.required),
            buyingPriceInput: new FormControl(''),
            sellingPriceInput: new FormControl(''),
            routeTypeDropdown: new FormControl('DOMESTIC', Validators.required),
            serviceCategoryDropdown: new FormControl('FULL_SERVICE', Validators.required)
        });
        this.syncFirstCargoLineValidation();
        this.fieldWorkService.laborRoles().subscribe({
            next: roles => this.workforceRoles = roles ?? [],
            error: () => this.workforceRoles = []
        });

        const preselectedProjectId = Number(this.route.snapshot.queryParamMap.get('projectId') || '') || undefined;
        this.breadcrumbItems = [
            { label: 'Shipments', routerLink: '/shipment' },
            { label: 'Create', routerLink: '/shipment/create' }
        ];
        this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };

        this.partyService.getParties(this.sessionStorageService.getCompany()?.id ?? 0, ['Company'], '', 0, 1000).subscribe(value => {
            this.companyList.set(value.content.map(party => this.partyService.convertPartyToCompany(party)));
        })

        // Initial load of customers for the current company
        const currentCompany = this.sessionStorageService.getCompany();
        if (currentCompany) {
            this.shipment.company = currentCompany;
			this.loadPaymentTerms(currentCompany.id);
            this.configureCommercialScope(currentCompany);
            if (preselectedProjectId) {
                this.shipment.logisticsProjectId = preselectedProjectId;
            }
            this.loadContainerJourneyTemplates(currentCompany.id);
        }
        this.loadWarehouseReferenceData();
        this.loadWarehouseItemOptions();

        this.loadLocationPage(0);

        // this.isEnabledEnterpriseFeatures is now a signal tied to the service
        this.canSaveShipment = this.permissionService.hasPermission(app_permissions.shipments.CREATE);
    }

    ngOnDestroy(): void {
        if (this.partyCustomerFilterTimer) {
            clearTimeout(this.partyCustomerFilterTimer);
        }
        if (this.locationFilterTimer) {
            clearTimeout(this.locationFilterTimer);
        }
    }

    canViewShipmentPricing(): boolean {
        return this.permissionService.hasPermission(app_permissions.shipments.pricing.view);
    }

    get owningCompanyId(): number | undefined {
        return this.sessionStorageService.getCompany()?.id;
    }

    switchToExpressShipment(): void {
        this.router.navigate(['/shipment/create/express']);
    }

    companySelected(_company?: Company): void {
        // Sender and receiver contacts are tenant Party Customers. Do not
        // replace their directory with a company-scoped/default-contact list.
    }

    onCommercialPartyChange(reference: CommercialPartyReference): void {
        const partyChanged = this.shipment.crmClientId !== reference.crmClientId
            || this.shipment.crmAccountId !== reference.crmAccountId
            || this.commercialPartyReference.source !== reference.source;
        this.commercialPartyReference = reference;
        this.shipment.crmClientId = reference.crmClientId ?? undefined;
        this.shipment.crmAccountId = reference.crmAccountId ?? undefined;
        this.shipment.crmClientContactId = reference.crmContactId ?? undefined;
        if (partyChanged) {
            this.contractOptions = [];
            this.contractOptionsLoading = false;
        }
        this.refreshContractPricingDecision();
        this.loadWarehouseItemOptions(true);
		if (!reference.crmClientId) {
			this.paymentTermId = this.paymentTermId ?? this.paymentTerms[0]?.paymentTermID;
		}

        const applyLegacy = (companyId?: number | null, customerId?: number | null): void => {
            if (companyId) {
                const company = this.companyList().find(item => item.id === companyId);
                if (company) {
                    this.shipment.company = company;
                    this.createShipmentForm.patchValue({ companyDropdown: company });
                    this.companySelected(company);
                }
            }
            if (customerId) {
                this.customerService.getCustomerById(customerId).subscribe(response => {
                    if (response.data) {
                        this.shipment.customer = response.data;
                        this.addCustomerOption(response.data);
                        this.createShipmentForm.patchValue({ customerDropdown: response.data });
                        this.refreshIntakePreviews();
                    }
                });
            }
            this.refreshIntakePreviews();
        };

        if (reference.source === 'LEGACY') {
            applyLegacy(reference.legacyCompanyId, reference.legacyCustomerId);
            return;
        }
        if (!this.owningCompanyId || !reference.crmClientId) return;
        this.crmService.getClients(this.owningCompanyId, ['CUSTOMER']).subscribe(clients => {
            const client = clients.find(item => item.id === reference.crmClientId);
			this.paymentTermId = client?.defaultPaymentTermId ?? this.paymentTerms[0]?.paymentTermID;
            applyLegacy(client?.linkedCompanyId, client?.linkedCustomerId);
        });
    }

    onPartyCustomerPickerShown(): void {
        this.loadPartyCustomerPage(0);
    }

    onPartyCustomerPickerLazyLoad(event: { first?: number; last?: number }): void {
        const pinnedCount = this.partyCustomerPins().length;
        const first = Math.max(0, (event.first ?? 0) - pinnedCount);
        const last = Math.max(first, (event.last ?? first + this.partyCustomerPageSize) - pinnedCount - 1);
        const firstPage = Math.floor(first / this.partyCustomerPageSize);
        const lastPage = Math.floor(last / this.partyCustomerPageSize);

        for (let page = firstPage; page <= lastPage; page++) {
            this.loadPartyCustomerPage(page);
        }
    }

    onPartyCustomerFilter(searchText: string): void {
        if (this.partyCustomerFilterTimer) {
            clearTimeout(this.partyCustomerFilterTimer);
        }

        this.partyCustomerFilterTimer = setTimeout(() => {
            const normalizedSearch = (searchText ?? '').trim();
            if (normalizedSearch === this.partyCustomerSearchText) return;

            this.partyCustomerSearchText = normalizedSearch;
            this.partyCustomerQueryVersion++;
            this.partyCustomerLoadedPages.clear();
            this.partyCustomerLoadingPages.clear();
            this.partyCustomerPage.set([]);
            this.partyCustomerOptionsLoading = false;
            this.loadPartyCustomerPage(0);
        }, 250);
    }

    onPartyCustomerPickerHidden(): void {
        if (this.partyCustomerFilterTimer) {
            clearTimeout(this.partyCustomerFilterTimer);
        }
        if (!this.partyCustomerSearchText) return;

        this.partyCustomerSearchText = '';
        this.partyCustomerQueryVersion++;
        this.partyCustomerLoadedPages.clear();
        this.partyCustomerLoadingPages.clear();
        this.partyCustomerPage.set([]);
        this.partyCustomerOptionsLoading = false;
    }

    onPartyCustomerSelected(customer?: Customer): void {
        this.addCustomerOption(customer);
        this.refreshIntakePreviews();
    }

    private loadPartyCustomerPage(page: number): void {
        const owningCompanyId = this.sessionStorageService.getCompany()?.id;
        if (!owningCompanyId || page < 0 || this.partyCustomerLoadedPages.has(page)) return;

        const queryVersion = this.partyCustomerQueryVersion;
        const requestKey = `${queryVersion}:${page}`;
        if (this.partyCustomerLoadingPages.has(requestKey)) return;

        this.partyCustomerLoadingPages.add(requestKey);
        this.partyCustomerOptionsLoading = true;
        this.partyService.getParties(
            owningCompanyId,
            [PartyType.Customer],
            this.partyCustomerSearchText,
            page,
            this.partyCustomerPageSize
        ).subscribe({
            next: response => {
                if (queryVersion !== this.partyCustomerQueryVersion) return;

                const total = Math.max(0, response.totalElements ?? response.content?.length ?? 0,
                    response.content?.length ?? 0);
                const pageOptions = this.partyCustomerPage();
                const nextPage = Array.from({ length: total }, (_, index) =>
                    pageOptions[index] ?? this.partyCustomerLoadingOption(index));
                (response.content ?? []).forEach((party, index) => {
                    const optionIndex = page * this.partyCustomerPageSize + index;
                    if (optionIndex < nextPage.length) {
                        nextPage[optionIndex] = this.partyService.convertPartyToCustomer(party);
                    }
                });
                this.partyCustomerPage.set(nextPage);
                this.partyCustomerLoadedPages.add(page);
            },
            error: () => {
                if (queryVersion !== this.partyCustomerQueryVersion) return;
                this.partyCustomerLoadingPages.delete(requestKey);
                this.partyCustomerOptionsLoading = this.partyCustomerLoadingPages.size > 0;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Party customers unavailable',
                    detail: 'The Party Customer directory could not be searched. Please try again.'
                });
            },
            complete: () => {
                this.partyCustomerLoadingPages.delete(requestKey);
                this.partyCustomerOptionsLoading = this.partyCustomerLoadingPages.size > 0;
            }
        });
    }

    isPartyCustomerLoadingOption(option: PartyCustomerOption | null | undefined): option is PartyCustomerLoadingOption {
        return !!option && 'partyCustomerLoading' in option;
    }

    private partyCustomerLoadingOption(index: number): PartyCustomerLoadingOption {
        return {
            // Customer IDs are positive database IDs. A unique negative ID
            // keeps virtual-scroll placeholders distinct and non-selectable.
            id: -1 - index,
            name: 'Loading contact…',
            partyCustomerLoading: true
        };
    }

    onLocationPickerLazyLoad(event: { first?: number; last?: number }): void {
        const pinnedCount = this.locationPins().length;
        const first = Math.max(0, (event.first ?? 0) - pinnedCount);
        const last = Math.max(first, (event.last ?? first + this.locationPageSize) - pinnedCount - 1);
        const firstPage = Math.floor(first / this.locationPageSize);
        const lastPage = Math.floor(last / this.locationPageSize);

        for (let page = firstPage; page <= lastPage; page++) {
            this.loadLocationPage(page);
        }
    }

    onLocationFilter(searchText: string): void {
        if (this.locationFilterTimer) {
            clearTimeout(this.locationFilterTimer);
        }

        this.locationFilterTimer = setTimeout(() => {
            const normalizedSearch = (searchText ?? '').trim();
            if (normalizedSearch === this.locationSearchText) return;

            this.locationSearchText = normalizedSearch;
            this.locationQueryVersion++;
            this.locationHasMore = true;
            this.locationLoadedPages.clear();
            this.locationLoadingPages.clear();
            this.locationPage.set([]);
            this.locationOptionsLoading = false;
            this.loadLocationPage(0);
        }, 300);
    }

    private loadLocationPage(page: number): void {
        if (page < 0 || this.locationLoadedPages.has(page) || (page > 0 && !this.locationHasMore)) return;

        const queryVersion = this.locationQueryVersion;
        const requestKey = `${queryVersion}:${page}`;
        if (this.locationLoadingPages.has(requestKey)) return;

        this.locationLoadingPages.add(requestKey);
        this.locationOptionsLoading = true;
        this.locationService.getAllComboData(this.locationSearchText, page, this.locationPageSize, 'en-US').subscribe({
            next: response => {
                if (queryVersion !== this.locationQueryVersion) return;

                const items = response.items ?? [];
                const total = Math.max(0, Number(response.count ?? items.length), items.length);
                const currentPage = this.locationPage();
                const nextPage = Array.from({ length: total }, (_, index) =>
                    currentPage[index] ?? this.locationLoadingOption(index)
                );
                items.forEach((location, index) => {
                    const optionIndex = page * this.locationPageSize + index;
                    if (optionIndex < nextPage.length) {
                        nextPage[optionIndex] = location;
                    }
                });
                this.locationPage.set(nextPage);
                this.locationLoadedPages.add(page);
                this.locationHasMore = response.hasMore;
                this.reconcileLoadedShipmentSelections();
            },
            error: () => {
                if (queryVersion !== this.locationQueryVersion) return;
                this.locationLoadingPages.delete(requestKey);
                this.locationOptionsLoading = this.locationLoadingPages.size > 0;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Locations unavailable',
                    detail: 'The location directory could not be searched. Please try again.'
                });
            },
            complete: () => {
                this.locationLoadingPages.delete(requestKey);
                this.locationOptionsLoading = this.locationLoadingPages.size > 0;
            }
        });
    }

    isLocationLoadingOption(option: ShipmentLocationOption | null | undefined): option is LocationLoadingOption {
        return !!option && 'locationLoading' in option;
    }

    private locationLoadingOption(index: number): LocationLoadingOption {
        return {
            // Location IDs are positive database IDs. A unique negative ID
            // keeps virtual-scroll placeholders distinct and non-selectable.
            id: -1 - index,
            name: 'Loading location…',
            locationLoading: true
        };
    }

    private pinLocation(location?: Location): void {
        if (!location?.id || this.locationPins().some(option => option.id === location.id)) return;
        this.locationPins.set([...this.locationPins(), location]);
    }

    private loadedLocation(location?: Location): Location | undefined {
        if (!location?.id) return location;
        return this.locationOptions().find(option =>
            !this.isLocationLoadingOption(option) && option.id === location.id
        ) as Location | undefined;
    }

    onLocationSelect(type: 'source' | 'destination') {
        this.pinLocation(type === 'source' ? this.shipment.sourceLocation : this.shipment.destinationLocation);
        this.placeSelect(); // Call your distance calculation function
        this.refreshContractPricingDecision();
        this.syncPartiesFromLocations(type);
        this.refreshIntakePreviews();
    }

    placeSelect(): void {
        if (this.shipment.sourceLocation && this.shipment.destinationLocation) {
            const distanceMatrixService = new google.maps.DistanceMatrixService();
            const parameters = {
                origins: [new google.maps.LatLng(this.shipment.sourceLocation.location.latitude?.valueOf()!!,
                    this.shipment.sourceLocation.location.longitude?.valueOf()!!)],
                destinations: [new google.maps.LatLng(this.shipment.destinationLocation.location.latitude?.valueOf()!!,
                    this.shipment.destinationLocation.location.longitude?.valueOf()!!)],
                travelMode: google.maps.TravelMode['DRIVING'],
                unitSystem: google.maps.UnitSystem.METRIC, // kilometers and meters.
                avoidHighways: false,
                avoidTolls: false
            };
            distanceMatrixService.getDistanceMatrix(parameters, (response: google.maps.DistanceMatrixResponse | null,
                status: google.maps.DistanceMatrixStatus) => {
                if (status != google.maps.DistanceMatrixStatus.OK) {
                } else {
                    if (response?.rows[0]?.elements[0]?.status === 'ZERO_RESULTS') {
                    } else {
                        const distance = response?.rows[0]?.elements[0]?.distance;
                        const duration = response?.rows[0]?.elements[0]?.duration;
                        this.shipment.distance = distance?.value!!;
                        this.shipment.time = duration?.value!!
                    }
                }
            });
        }

    }

    vehicleTypeSelected(): void {
        if (this.shipment.vehicleParaPack?.vehicleType) {
            this.vehicleConfigurations = this.shipment.vehicleParaPack.vehicleType.vehicleConfigs ?? [];
            this.vehicleCapacities = this.shipment.vehicleParaPack.vehicleType.vehicleCapacities ?? [];
            if (!this.vehicleConfigurations.some(config => config.configId === this.shipment.vehicleParaPack.vehicleConfig?.configId)) {
                this.shipment.vehicleParaPack.vehicleConfig = undefined as any;
                this.createShipmentForm.patchValue({ vehicleConfigDropdown: null });
            }
            if (!this.vehicleCapacities.some(capacity => capacity.capacityId === this.shipment.vehicleParaPack.vehicleCapacity?.capacityId)) {
                this.shipment.vehicleParaPack.vehicleCapacity = undefined as any;
                this.createShipmentForm.patchValue({ vehicleCapacityDropdown: null });
            }
        } else {
            this.vehicleConfigurations = [];
            this.vehicleCapacities = [];
            this.shipment.vehicleParaPack.vehicleConfig = undefined as any;
            this.shipment.vehicleParaPack.vehicleCapacity = undefined as any;
            this.createShipmentForm.patchValue({ vehicleConfigDropdown: null, vehicleCapacityDropdown: null });
        }
        this.refreshContractPricingDecision();
    }

    refreshContractPricingDecision(manualSelection = false): void {
        if (!this.canViewShipmentPricing() || this.rateSource !== 'RATE_CARD') {
            this.rateMatchSequence++;
            return;
        }
        const request = this.toContractPricingDecisionRequest();
        const key = JSON.stringify([request.sellingCompanyId, request.buyingCompanyId, request.crmClientId,
            request.crmAccountId, request.sourceLocationId, request.destinationLocationId,
            request.pickupWindowStart, request.vehicleTypeId, request.vehicleConfigId,
            request.vehicleCapacityId, request.currencyCode]);
        if (!manualSelection && key === this.rateMatchKey) return;
        this.rateMatchKey = key;
        if (!manualSelection) {
            request.contractId = request.contractRevisionId = request.rateCardId = undefined;
            if (this.pricingRateCardId) this.shipment.sellingPrice = undefined;
            this.pricingContractId = this.pricingContractRevisionId = this.pricingRateCardId = undefined;
            this.selectedPricingContract = this.selectedPricingRateCard = undefined;
            this.rateCardOptions = [];
            this.pricingWarnings = [];
        }
        const sequence = ++this.rateMatchSequence;
        if (!request.sellingCompanyId || (!request.buyingCompanyId && !request.crmClientId) || !request.sourceLocationId
            || !request.destinationLocationId || !request.vehicleTypeId) {
            return;
        }
        this.contractService.decideRateCard(request).subscribe({ next: decision => {
            if (sequence !== this.rateMatchSequence) return;
            this.pricingWarnings = decision.warnings ?? [];
            if (decision.matched && decision.rateCard) {
                this.rateSource = 'RATE_CARD';
                this.manualOverrideReason = '';
                this.pricingContractId = decision.contractId;
                this.pricingContractRevisionId = decision.contractRevisionId;
                this.pricingRateCardId = decision.rateCardId;
                this.shipment.sellingPrice = decision.sellingPrice;
                this.selectedPricingRateCard = this.toRateCardOption(decision.rateCard);
                this.rateCardOptions = [this.selectedPricingRateCard];
                if (decision.contractId) this.contractService.getContractById(decision.contractId).subscribe({
                    next: contract => {
                        if (sequence !== this.rateMatchSequence) return;
                        this.selectedPricingContract = contract;
                        this.contractOptions = this.mergeContracts(this.contractOptions, [contract]);
                    },
                    error: () => {}
                });
            } else {
                if (this.pricingRateCardId) this.shipment.sellingPrice = undefined;
                this.selectedPricingRateCard = undefined;
                this.pricingRateCardId = undefined;
                this.rateCardOptions = [];
            }
            this.refreshIntakePreviews();
        }, error: () => {
            if (sequence === this.rateMatchSequence) this.pricingWarnings = ['Customer rate card matching is unavailable. Change a selection to retry.'];
        }});
    }

    submitOrder(openDeliveryAssignment = false) {
        try {
            this.shipmentCreationInProgress = true;
            this.submissionIssues = [];
            this.continueToDeliveryAssignment = openDeliveryAssignment;
            this.shipment.requestedBookingOption = BookingOption.BOOK_LATER;
            this.shipment.requestedBookingTime = this.pickupDate?.valueOf()!!;
            this.shipment.customer = this.shipment.customer || this.sessionStorageService.getCustomer()!!;

            this.shipment.shipmentType = ShipmentType.ENTERPRISE;
            this.shipment.routeType = this.createShipmentForm.get('routeTypeDropdown')?.value;
            this.shipment.serviceCategory = this.createShipmentForm.get('serviceCategoryDropdown')?.value;

            this.prepareCargoLines();

            this.shipment.creationDate = Date.now();
            this.shipment.shipmentStatus = 'DRAFT';

            if (this.shipment.vehicleParaPack?.vehicleType) {
                this.shipment.vehicleParaPack.vehicleType = {
                    ...this.shipment.vehicleParaPack.vehicleType,
                    vehicleConfigs: [],
                    vehicleCapacities: []
                } as VehicleType;
            }

            if (this.shipment.company?.id != this.sessionStorageService.getCompany()?.id) {
                this.shipment.owningCompany = this.sessionStorageService.getCompany()!;
                this.shipment.owningCompanyContact = this.sessionStorageService.getCustomer()!
            }
            const validationIssues: string[] = [];
            validationIssues.push(...this.shipment.payloadItemList.map(line => this.outboundPickupIssue(line)).filter(Boolean));
            if (this.shipment.sourceLocation == null)
                validationIssues.push('Source location needs to be entered.');
            if (this.shipment.destinationLocation == null)
                validationIssues.push('Destination location needs to be entered.');
            validationIssues.push(...this.locationCoordinateValidationIssues());
            if (this.shipment.requestedBookingTime == null)
                validationIssues.push('Pickup date needs to be entered.');
            if (this.hasInvalidDeliveryWindow()) {
                validationIssues.push('Delivery window must be on or after the pickup window.');
            }
            if (this.shipment.payloadItemList.length == 0 || this.shipment.payloadItemList.some(line => !this.isContainerLine(line) && !this.isWarehouseItemLine(line) && line.itemType == null))
                validationIssues.push('Cargo type should be added.');
            if (this.shipment.payloadItemList.length == 0 || this.shipment.payloadItemList.some(line => !this.isContainerLine(line) && !line.itemQuantity?.quantity))
                validationIssues.push('Pallet count should be added.');
            const containerNumbers = this.shipment.payloadItemList.filter(line => this.isContainerLine(line))
                .map(line => line.containerNumber?.trim().toUpperCase()).filter(Boolean);
            if (new Set(containerNumbers).size !== containerNumbers.length)
                validationIssues.push('Each container number must be unique within the shipment.');
            if (this.shipment.payloadItemList.some((line, index) => !this.isContainerLine(line) && !this.isWarehouseItemLine(line) && (!this.cargoTotalWeights[index] || this.cargoTotalWeights[index] <= 0)))
                validationIssues.push('Total weight should be added.');
            if (this.requiresManualOverrideReason() && !this.manualOverrideReason?.trim())

                validationIssues.push('Manual cost override reason should be added.');
			if (!this.paymentTermId)
				validationIssues.push('Commercial payment term should be selected.');

            if (validationIssues.length > 0) {
                const issues = this.setSubmissionIssues(validationIssues, 'Review the required intake fields and try again.');
                this.shipmentCreationInProgress = false;
                this.messageService.add({
                    severity: 'error', summary: 'Order creation failed!',
                    detail: issues.join(' ')
                });
                return;
            }
        }
        catch (e) {
            const errorMessage = e instanceof Error ? e.message : 'Unable to prepare the shipment for saving.';
            const issues = this.setSubmissionIssues([], errorMessage);
            this.shipmentCreationInProgress = false;
            this.messageService.add({
                severity: 'error', summary: 'Order creation failed!',
                detail: issues.join(' ')
            });
            return;
        }

        let intakeRequest: ShipmentIntakeRequest;
        try {
            intakeRequest = this.toIntakeRequest();
        } catch (error) {
            const message = error instanceof Error ? error.message : 'Unable to prepare the shipment for saving.';
            this.setSubmissionIssues([], message);
            this.shipmentCreationInProgress = false;
            return;
        }
        console.log('Shipment Intake Request: ' + JSON.stringify(intakeRequest));
        const saveShipment$ = this.shipmentIntakeService.submit(intakeRequest);

        saveShipment$.subscribe(createdShipmentResponse => {
            console.log('Shipment Intake Response: ' + JSON.stringify(createdShipmentResponse));
            this.intakeReadiness = createdShipmentResponse.readiness;
            this.routePreview = createdShipmentResponse.routePreview;
                this.pricingWarnings = createdShipmentResponse.pricing?.warnings ?? [];
                this.pricingPreview = createdShipmentResponse.pricing;
            if (createdShipmentResponse?.shipmentId) {
                this.submissionIssues = [];
                this.finishShipmentSave(createdShipmentResponse);
            } else {
                const issues = this.setSubmissionIssues(
                    createdShipmentResponse.validationIssues,
                    'The shipment could not be saved. Review the required intake fields and try again.'
                );
                this.messageService.add({
                    severity: 'error', summary: 'Order creation failed!',
                    detail: issues.join(' ')
                });
                this.shipmentCreationInProgress = false;
            }
        }, error => {
            const validationIssues = error?.error?.validationIssues || error?.error?.readiness?.issues;
            this.intakeReadiness = error?.error?.readiness;
            this.routePreview = error?.error?.routePreview;
            this.pricingWarnings = error?.error?.pricing?.warnings ?? [];
            this.pricingPreview = error?.error?.pricing;
            const issues = this.setSubmissionIssues(error?.status >= 500 ? [] : validationIssues,
                this.intakeSaveErrorMessage(error, validationIssues));
            this.messageService.add({
                severity: 'error', summary: 'Order creation failed!',
                detail: issues.join(' ')
            });
            this.shipmentCreationInProgress = false;
        });
    }

    private finishShipmentSave(createdShipmentResponse: ShipmentIntakeResponse): void {
        const shipmentId = createdShipmentResponse.shipmentId;
        if (!shipmentId) {
            this.shipmentCreationInProgress = false;
            return;
        }

        const navigateAfterSave = () => {
            this.messageService.add({
                severity: 'success', summary: 'Shipment# ' + createdShipmentResponse.shipmentNumber + ' is created',
                detail: 'Shipment# ' + createdShipmentResponse.shipmentNumber
                    + ' has been successfully created. Our team will notify you when trucks are assigned to your order'
            });
            this.router.navigate(this.continueToDeliveryAssignment
                ? ['/shipment/delivery', shipmentId, 'generate']
                : ['/shipment/view', shipmentId]);
        };

        if (!this.shipmentDocumentQueue.length) {
            this.linkProjectRequirementFulfillment(shipmentId, createdShipmentResponse.shipmentNumber).add(() => {
                this.shipmentCreationInProgress = false;
                navigateAfterSave();
            });
            return;
        }

        this.messageService.add({
            severity: 'info',
            summary: 'Uploading documents',
            detail: `${this.shipmentDocumentQueue.length} document${this.shipmentDocumentQueue.length === 1 ? '' : 's'} will be attached to the shipment.`
        });
        this.uploadQueuedShipmentDocuments(shipmentId)
            .then(() => {
                this.linkProjectRequirementFulfillment(shipmentId, createdShipmentResponse.shipmentNumber).add(() => {
                    this.shipmentCreationInProgress = false;
                    navigateAfterSave();
                });
            })
            .catch(() => {
                this.messageService.add({
                    severity: 'warn',
                    summary: 'Shipment saved',
                    detail: 'The shipment was saved, but one or more documents could not be attached.'
                });
                this.linkProjectRequirementFulfillment(shipmentId, createdShipmentResponse.shipmentNumber).add(() => {
                    this.shipmentCreationInProgress = false;
                    this.router.navigate(this.continueToDeliveryAssignment
                        ? ['/shipment/delivery', shipmentId, 'generate']
                        : ['/shipment/view', shipmentId]);
                });
            });
    }

    private linkProjectRequirementFulfillment(targetResourceId: number, sourceReference?: string) {
        const projectId = Number(this.route.snapshot.queryParamMap.get('projectId') || this.shipment.logisticsProjectId || 0);
        const requirementId = Number(this.route.snapshot.queryParamMap.get('requirementId') || 0);
        const owningCompanyId = Number(this.sessionStorageService.getCompany()?.id || 0);
        if (!projectId || !requirementId || !owningCompanyId || !targetResourceId) {
            return { add: (callback: () => void) => callback() };
        }
        return this.logisticsProjectsService.linkRequirementFulfillment(projectId, owningCompanyId, requirementId, {
            targetResourceType: this.route.snapshot.queryParamMap.get('targetResourceType') || 'SHIPMENT',
            targetResourceId,
            sourceModule: 'SHIPMENT',
            sourceReference: sourceReference || `Shipment #${targetResourceId}`,
            fulfilledQuantity: Number(this.route.snapshot.queryParamMap.get('quantity') || 1),
            fulfillmentStatus: 'LINKED',
            status: 'ACTIVE'
        }).subscribe({
            error: error => this.messageService.add({
                severity: 'warn',
                summary: 'Requirement link',
                detail: error?.error?.message || 'Shipment was saved, but requirement fulfillment was not linked.'
            })
        });
    }

    private reconcileLoadedShipmentSelections(): void {
        if (!this.shipment) return;

        if (this.shipment.sourceLocation?.id) {
            this.pinLocation(this.shipment.sourceLocation);
            this.shipment.sourceLocation = this.loadedLocation(this.shipment.sourceLocation) ?? this.shipment.sourceLocation;
        }
        if (this.shipment.destinationLocation?.id) {
            this.pinLocation(this.shipment.destinationLocation);
            this.shipment.destinationLocation = this.loadedLocation(this.shipment.destinationLocation) ?? this.shipment.destinationLocation;
        }
        this.routeStops.forEach(stop => {
            this.pinLocation(stop.location);
            stop.location = this.loadedLocation(stop.location) ?? stop.location;
        });
        if (this.shipment.vehicleParaPack?.vehicleType?.id && this.vehicleTypes.length > 0) {
            this.shipment.vehicleParaPack.vehicleType = this.vehicleTypes.find(type => type.id === this.shipment.vehicleParaPack.vehicleType.id) ?? this.shipment.vehicleParaPack.vehicleType;
            this.vehicleConfigurations = this.shipment.vehicleParaPack.vehicleType?.vehicleConfigs ?? [];
            this.vehicleCapacities = this.shipment.vehicleParaPack.vehicleType?.vehicleCapacities ?? [];
        }
        if (this.shipment.vehicleParaPack?.vehicleConfig?.configId && this.vehicleConfigurations.length > 0) {
            this.shipment.vehicleParaPack.vehicleConfig = this.vehicleConfigurations.find(config => config.configId === this.shipment.vehicleParaPack.vehicleConfig.configId) ?? this.shipment.vehicleParaPack.vehicleConfig;
        }
        if (this.shipment.vehicleParaPack?.vehicleCapacity?.capacityId && this.vehicleCapacities.length > 0) {
            this.shipment.vehicleParaPack.vehicleCapacity = this.vehicleCapacities.find(capacity => capacity.capacityId === this.shipment.vehicleParaPack.vehicleCapacity.capacityId) ?? this.shipment.vehicleParaPack.vehicleCapacity;
        }
        if (this.shipment.payloadItemList?.length && this.payloadTypes.length > 0) {
            this.shipment.payloadItemList.forEach(line => {
                if (line.itemType?.typeId) {
                    line.itemType = this.payloadTypes.find(type => type.typeId === line.itemType?.typeId) ?? line.itemType;
                }
                line.itemQuantity = line.itemQuantity ?? new QuantityView();
                line.lineSource = line.inventoryItemId ? 'WAREHOUSE_ITEM' : (line.lineSource ?? 'GENERAL_CARGO');
            });
        }

        this.createShipmentForm?.patchValue({
            companyDropdown: this.shipment.company,
            customerDropdown: this.shipment.customer,
            sourceLocationDropdown: this.shipment.sourceLocation,
            destinationLocationDropdown: this.shipment.destinationLocation,
            pickupDateSelect: this.pickupDate,
            vehicleTypeDropdown: this.shipment.vehicleParaPack?.vehicleType,
            vehicleConfigDropdown: this.shipment.vehicleParaPack?.vehicleConfig,
            vehicleCapacityDropdown: this.shipment.vehicleParaPack?.vehicleCapacity,
            quantityRequiredInput: this.shipment.quantityRequired,
            payloadTypeDropdown: this.shipment.payloadItemList?.[0]?.itemType,
            uomQuantityInput: this.shipment.payloadItemList?.[0]?.itemQuantity?.quantity,
            itemWeightInput: this.totalWeight,
            buyingPriceInput: this.usesInternalCostField()
                ? this.shipment.pricingInternalCostAmount
                : this.shipment.buyingPrice,
            sellingPriceInput: this.shipment.sellingPrice,
            routeTypeDropdown: this.shipment.routeType || 'DOMESTIC',
            serviceCategoryDropdown: this.shipment.serviceCategory || 'FULL_SERVICE'
        });
        this.syncFirstCargoLineValidation();
    }

	private loadPaymentTerms(owningCompanyId: number): void {
		this.ordersService.getAllPaymentTerms(owningCompanyId).subscribe({
			next: terms => {
				this.paymentTerms = terms ?? [];
				if (!this.paymentTermId) this.paymentTermId = this.paymentTerms[0]?.paymentTermID;
			},
			error: () => this.paymentTerms = []
		});
	}

    private editableRouteStopsFromWaypoints(waypoints: ShipmentWaypoint[]): RouteStopDraft[] {
        const orderedWaypoints = [...waypoints].sort((left, right) => (left.waypointNumber ?? 0) - (right.waypointNumber ?? 0));
        return orderedWaypoints
            .filter((waypoint, index) => !this.isPrimaryEndpointWaypoint(waypoint, index, orderedWaypoints.length))
            .map(waypoint => ({
                waypointId: waypoint.waypointId,
                location: waypoint.location,
                stopType: waypoint.waypointType || ShipmentWaypointType.DROPOFF,
                scheduledArrival: waypoint.scheduledArrival ? new Date(waypoint.scheduledArrival as any) : null,
                scheduledDeparture: waypoint.scheduledDeparture ? new Date(waypoint.scheduledDeparture as any) : null,
                remarks: waypoint.remarks
            }));
    }

    private isPrimaryEndpointWaypoint(waypoint: ShipmentWaypoint, index: number, waypointCount: number): boolean {
        return (index === 0 && waypoint.waypointType === ShipmentWaypointType.PICKUP)
            || (index === waypointCount - 1 && waypoint.waypointType === ShipmentWaypointType.DROPOFF);
    }

    openLocationDialog(type: 'source' | 'destination') {
        const ref = this.dialogService.open(CreateLocationComponent, {
            header: 'Create/Update Location',
            modal: true,
            width: '70%',
            data: { embedded: true },
            styleClass: 'shipment-location-dialog'
        });

        ref.onClose.subscribe((location: Location) => {
            if (location) {
                this.pinLocation(location);
                if (type === 'source') {
                    this.shipment.sourceLocation = location;
                    this.createShipmentForm.patchValue({ sourceLocationDropdown: location });
                } else {
                    this.shipment.destinationLocation = location;
                    this.createShipmentForm.patchValue({ destinationLocationDropdown: location });
                }
                this.syncPartiesFromLocations();
                this.refreshIntakePreviews();
            }
        });
    }

    openStopLocationDialog(index: number): void {
        const ref = this.dialogService.open(CreateLocationComponent, {
            header: 'Create/Update Location',
            modal: true,
            width: '70%',
            data: { embedded: true },
            styleClass: 'shipment-location-dialog'
        });

        ref.onClose.subscribe((location: Location) => {
            if (!location) {
                return;
            }
            this.pinLocation(location);
            if (this.routeStops[index]) {
                this.routeStops[index].location = location;
                this.onStopLocationSelected(this.routeStops[index]);
            }
        });
    }

    registerCompany() {
        this.companyFormHeader = 'Create New Company';
        this.companyFormSubmitLabel = 'Create';
        this.selectedCustomer = new Customer(); // Clear for new customer
        this.selectedCompany = new Company(); // Clear for new company
        this.showCompanyForm = true;
    }

    registerContact(target?: 'sender' | 'receiver' | 'stop', stopIndex?: number) {
        const contactCompany = target === 'sender'
            ? this.selectedSenderCompany ?? this.shipment.sourceLocation?.company ?? this.shipment.company
            : target === 'receiver'
                ? this.selectedReceiverCompany ?? this.shipment.destinationLocation?.company ?? this.shipment.company
                : target === 'stop'
                    ? this.routeStops[stopIndex ?? -1]?.location?.company ?? this.shipment.company
                    : this.shipment.company;
        if (!contactCompany?.id) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Select a company first',
                detail: 'Select the related pickup or drop-off location before adding its contact.'
            });
            return;
        }
        this.contactCreationTarget = target;
        this.contactCreationStopIndex = target === 'stop' ? stopIndex : undefined;
        this.customerFormHeader = target === 'sender' ? 'Add Sender Contact'
            : target === 'receiver' ? 'Add Receiver Contact'
                : target === 'stop' ? 'Add Stop Contact' : 'Create New Customer';
        this.customerFormSubmitLabel = 'Create';
        this.selectedCustomer = new Customer();
        this.selectedCompany = contactCompany;
        this.showCustomerForm = true;
    }

    onCustomerFormClosed(createdCustomer?: Customer | boolean) {
        this.showCustomerForm = false;
        const target = this.contactCreationTarget;
        const stopIndex = this.contactCreationStopIndex;
        this.contactCreationTarget = undefined;
        this.contactCreationStopIndex = undefined;
        if (!createdCustomer || typeof createdCustomer === 'boolean') {
            return;
        }
        this.addCustomerOption(createdCustomer);
        if (target === 'sender') {
            this.selectedSenderContact = createdCustomer;
        } else if (target === 'receiver') {
            this.selectedReceiverContact = createdCustomer;
        } else if (target === 'stop' && stopIndex != null && this.routeStops[stopIndex]) {
            this.routeStops[stopIndex].contact = createdCustomer;
        } else {
            this.shipment.customer = createdCustomer;
            this.createShipmentForm.patchValue({ customerDropdown: createdCustomer });
        }
        this.refreshIntakePreviews();
    }

    onCompanyFormClosed() {
        this.showCompanyForm = false;
        let countryId = this.sessionStorageService.getCountryList()!.find(country => country.countryCode == 'SA')!.id;
        this.companyService.getCompaniesByCountry(countryId).subscribe(companies => {
            this.companyList.set(companies);
            this.shipment.company = this.selectedCompany;
        });
    }

    clearShipmentForm(): void {
        this.createShipmentForm.reset({
            routeTypeDropdown: 'DOMESTIC',
            serviceCategoryDropdown: 'FULL_SERVICE'
        });
        const currentCompany = this.sessionStorageService.getCompany();
        this.shipment = new Shipment();
        this.shipment.payloadItemList.push(this.createCargoLine());
        this.shipment.countryOfOrder = this.sessionStorageService.getCountryList()?.find(country => country.countryCode == 'SA')!!;
        if (currentCompany) {
            this.shipment.company = currentCompany;
        }
        this.pickupDate = null;
        this.deliveryWindowEnd = null;
        this.totalWeight = 0;
        this.cargoTotalWeights = [0];
        this.routeStops = [];
        this.selectedSenderCompany = undefined;
        this.selectedSenderContact = undefined;
        this.selectedReceiverCompany = undefined;
        this.selectedReceiverContact = undefined;
        this.selectedWatcher = undefined;
        this.watcherList = [];
        this.shipmentReference = '';
        this.shipmentNotes = '';
        this.priority = 'NORMAL';
        this.currencyCode = 'SAR';
        this.rateSource = 'RATE_CARD';
        this.rateMatchKey = '';
        this.rateMatchSequence++;
        this.buyingCostSource = 'OWNED';
        this.manualOverrideReason = '';
        this.buyingPriceEditedManually = false;
        this.lastRecommendedBuyingPrice = undefined;
        this.pricingContractId = undefined;
        this.pricingContractRevisionId = undefined;
        this.pricingRateCardId = undefined;
        this.selectedPricingContract = undefined;
        this.selectedPricingRateCard = undefined;
        this.rateCardOptions = [];
        this.intakeReadiness = undefined;
        this.routePreview = undefined;
        this.pricingPreview = undefined;
        this.pricingWarnings = [];
        this.submissionIssues = [];
        this.syncFirstCargoLineValidation();
    }

    addCargoLine(): void {
        this.shipment.payloadItemList.push(this.createCargoLine());
        this.cargoTotalWeights.push(0);
    }

    isWarehouseItemLine(line: PayloadItemView): boolean {
        return line.lineSource === 'WAREHOUSE_ITEM' || line.inventoryItemId != null;
    }

    isContainerLine(line: PayloadItemView): boolean {
        return line.lineSource === 'CONTAINER';
    }

    addContainerLine(source: PayloadItemView): void {
        const line = this.createCargoLine();
        line.lineSource = 'CONTAINER';
        line.isoEquipmentType = source.isoEquipmentType;
        line.bookingNumber = source.bookingNumber;
        this.shipment.payloadItemList.push(line);
        this.cargoTotalWeights.push(0);
        this.refreshIntakePreviews();
    }

    onCargoLineSourceChange(line: PayloadItemView, index: number): void {
        const selectedSource = line.lineSource === 'CONTAINER'
            ? 'CONTAINER'
            : line.lineSource === 'WAREHOUSE_ITEM'
                ? 'WAREHOUSE_ITEM'
                : 'GENERAL_CARGO';

        if (selectedSource === 'WAREHOUSE_ITEM') {
            line.itemType = undefined;
            if (index === 0) this.createShipmentForm.get('payloadTypeDropdown')?.setValue(null);
        } else {
            // Clear the warehouse-only snapshot without resetting the source the
            // operator just chose. Previously this helper changed CONTAINER back
            // to GENERAL_CARGO before the container template could render.
            this.clearWarehouseItemSnapshot(line, true);
            if (line.itemQuantity) line.itemQuantity.uom = undefined;
            this.cargoTotalWeights[index] = 0;
        }
        line.lineSource = selectedSource;
        this.syncFirstCargoLineValidation();
        this.refreshIntakePreviews();
    }

    onWarehouseItemChange(line: PayloadItemView, index: number): void {
        const item = this.warehouseItemOptions.find(option => option.inventoryItemId === line.inventoryItemId);
        if (!item) {
            this.clearWarehouseItemSnapshot(line, true);
            this.cargoTotalWeights[index] = 0;
            this.refreshIntakePreviews();
            return;
        }
        line.lineSource = 'WAREHOUSE_ITEM';
        line.warehouseItemNumber = item.itemNumber;
        line.skuNumber = item.skuNumber;
        line.customerItemNumber = item.customerItemNumber;
        line.warehouseItemName = item.itemName;
        line.trackingMode = item.trackingMode;
        line.itemDesciption = item.itemDescription || item.itemName || item.itemNumber;
        line.sourceWarehouseId = this.shipment.warehouseFlowDirection === 'INBOUND'
            ? undefined : item.defaultWarehouseId;
        line.conditionId = item.defaultConditionId;
        line.itemLength = item.length;
        line.itemBreadth = item.breadth;
        line.itemHeight = item.height;
        line.lengthUnit = this.lengthUnitList.find(unit => unit.lengthId === item.lengthUnitId);
        line.itemWeight = item.weightKg;
        line.itemQuantity = line.itemQuantity ?? new QuantityView();
        line.itemQuantity.uom = this.warehouseItemUoms(line).find(
            uom => Number(uom.uomId) === Number(item.defaultTenantUomId ?? item.defaultUomId)
        ) ?? this.warehouseItemUoms(line)[0];
        line.weightUnit = this.findKilogramWeightUnit();
        this.syncFirstCargoLineValidation();
        this.onWarehouseUomChange(line, index);
    }

    onCargoQuantityChange(line: PayloadItemView, index: number): void {
        if (this.isWarehouseItemLine(line)) {
            const item = this.warehouseItemOptions.find(option => option.inventoryItemId === line.inventoryItemId);
            this.cargoTotalWeights[index] = (item?.weightKg ?? line.itemWeight ?? 0)
                * (line.itemQuantity?.quantity ?? 0) * (line.factorToBase ?? 1);
        }
        this.refreshIntakePreviews();
    }

    onWarehouseUomChange(line: PayloadItemView, index: number): void {
        const item = this.selectedWarehouseItem(line);
        const selected = item?.tenantUomOptions?.find(
            option => option.tenantUomId === line.itemQuantity?.uom?.uomId);
        line.tenantUomId = selected?.tenantUomId;
        line.tenantUomName = selected?.name;
        line.factorToBase = selected?.factorToBase;
        this.onCargoQuantityChange(line, index);
    }

    warehouseItemUoms(line: PayloadItemView): any[] {
        const item = this.warehouseItemOptions.find(option => option.inventoryItemId === line.inventoryItemId);
        if (item?.tenantUomOptions?.length) {
            const cached = this.warehouseUomCache.get(item);
            if (cached) return cached;
            const choices = item.tenantUomOptions.map(option => ({
                uomId: option.tenantUomId, name: option.name,
                shortName: option.packLabel || option.name
            }));
            this.warehouseUomCache.set(item, choices);
            return choices;
        }
        if (item?.defaultTenantUomId != null) return [];
        const compatibleIds = item?.compatibleUomIds ?? [];
        return compatibleIds.length
            ? this.uomList.filter(uom => compatibleIds.includes(Number(uom.uomId)))
            : (this.findEachUom() ? [this.findEachUom()] : []);
    }

    warehouseItemUomMessage(line: PayloadItemView): string | null {
        if (!this.isWarehouseItemLine(line)) {
            return null;
        }
        const item = this.selectedWarehouseItem(line);
        if (!item) {
            return 'Select a warehouse item to load its permitted UOMs.';
        }
        if (item.defaultTenantUomId != null && !item.tenantUomOptions?.length) {
            return 'The item base Tenant UOM is inactive or unavailable. Correct the item master before shipping.';
        }
        if (!(item.tenantUomOptions?.length) && !(item.compatibleUomIds?.length)) {
            return this.findEachUom()
                ? 'No UOM is configured on this warehouse item. Each is selected as the system fallback.'
                : 'This warehouse item has no permitted UOM configured. Add Each, a base, sales, purchase, or conversion UOM to the item master.';
        }
        if (!line.itemQuantity?.uom?.uomId
            || (item.tenantUomOptions?.length
                ? !item.tenantUomOptions.some(option => option.tenantUomId === line.tenantUomId)
                : !item.compatibleUomIds?.includes(Number(line.itemQuantity.uom.uomId)))) {
            return 'Choose one of the UOMs configured for this warehouse item.';
        }
        return null;
    }

    selectedWarehouseItem(line: PayloadItemView): ShipmentIntakeWarehouseItemOption | undefined {
        return this.warehouseItemOptions.find(option => option.inventoryItemId === line.inventoryItemId);
    }

    warehouseItemDimensions(line: PayloadItemView): string {
        const item = this.selectedWarehouseItem(line);
        const dimensions = [item?.length ?? line.itemLength, item?.breadth ?? line.itemBreadth, item?.height ?? line.itemHeight];
        if (dimensions.every(value => value == null)) return 'Not configured';
        const unit = line.lengthUnit?.shortName || line.lengthUnit?.fullName || 'm';
        return `${dimensions.map(value => value ?? '-').join(' x ')} ${unit}`;
    }

    private createCargoLine(): PayloadItemView {
        const line = new PayloadItemView();
        line.lineSource = 'GENERAL_CARGO';
        line.itemQuantity = new QuantityView();
        return line;
    }

    private clearWarehouseItemSnapshot(line: PayloadItemView, preserveMode = false): void {
        line.inventoryItemId = undefined;
        line.tenantUomId = undefined;
        line.tenantUomName = undefined;
        line.factorToBase = undefined;
        line.warehouseItemNumber = undefined;
        line.skuNumber = undefined;
        line.customerItemNumber = undefined;
        line.warehouseItemName = undefined;
        line.trackingMode = undefined;
        line.sourceWarehouseId = undefined;
        line.conditionId = undefined;
        line.itemLength = undefined;
        line.itemBreadth = undefined;
        line.itemHeight = undefined;
        line.lengthUnit = undefined;
        line.itemWeight = undefined;
        line.weightUnit = undefined;
        line.itemDesciption = undefined;
        if (!preserveMode) line.lineSource = 'GENERAL_CARGO';
    }

    private syncFirstCargoLineValidation(): void {
        const firstLine = this.shipment.payloadItemList?.[0];
        const isContainer = !!firstLine && this.isContainerLine(firstLine);
        const isWarehouseItem = !!firstLine && !isContainer && this.isWarehouseItemLine(firstLine);
        const requiredControls = {
            payloadTypeDropdown: !isContainer && !isWarehouseItem,
            uomQuantityInput: !isContainer,
            itemWeightInput: !isContainer && !isWarehouseItem
        };
        for (const [name, required] of Object.entries(requiredControls)) {
            const control = this.createShipmentForm?.get(name);
            if (!control) continue;
            if (required) control.setValidators(Validators.required);
            else control.clearValidators();
            control.updateValueAndValidity({ emitEvent: false });
        }
    }

    intakeFormBlockingIssues(): string[] {
        if (!this.canSaveShipment) return ['You do not have permission to create shipments.'];
        const labels: Record<string, string> = {
            sourceLocationDropdown: 'Pickup location',
            destinationLocationDropdown: 'Drop-off location',
            pickupDateSelect: 'Pickup window',
            quantityRequiredInput: 'Requested truck count',
            payloadTypeDropdown: 'Cargo type',
            uomQuantityInput: 'Cargo quantity',
            itemWeightInput: 'Total cargo weight',
            routeTypeDropdown: 'Route type',
            serviceCategoryDropdown: 'Service category'
        };
        return Object.entries(labels)
            .filter(([name]) => this.createShipmentForm?.get(name)?.invalid)
            .map(([, label]) => `${label} is required.`);
    }

    private loadWarehouseReferenceData(): void {
        this.warehousingService.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }).subscribe({
            next: response => {
                this.warehouseOptions = (response.data || []).filter(
                    warehouse => warehouse.warehouseOwnershipType !== 'THIRD_PARTY_VIRTUAL'
                );
            },
            error: () => this.warehouseOptions = []
        });
        this.warehousingService.getMasterData('ITEM_CONDITION', { activeStatus: 'ACTIVE' }).subscribe({
            next: response => this.conditionOptions = response.data || [],
            error: () => this.conditionOptions = []
        });
    }

    private loadContainerJourneyTemplates(owningCompanyId: number): void {
        this.containerJourneyTemplatesLoading = true;
        this.containerTrackingService.findJourneyTemplates(owningCompanyId).subscribe({
            next: templates => {
                this.containerJourneyTemplates = templates ?? [];
                this.containerJourneyTemplatesLoading = false;
            },
            error: () => {
                this.containerJourneyTemplates = [];
                this.containerJourneyTemplatesLoading = false;
            }
        });
    }

    private loadWarehouseItemOptions(clearMissingSelections = false): void {
        const loadSequence = ++this.warehouseItemLoadSequence;
        this.warehouseItemsLoading = true;
        this.warehouseItemsMessage = '';
        this.shipmentIntakeService.searchWarehouseItems({
            crmClientId: this.shipment.crmClientId,
            crmAccountId: this.shipment.crmAccountId
        }).subscribe({
            next: items => {
                if (loadSequence !== this.warehouseItemLoadSequence) return;
                this.applyWarehouseItemOptions(items ?? [], clearMissingSelections);
            },
            error: () => {
                if (loadSequence !== this.warehouseItemLoadSequence) return;
                this.warehouseItemOptions = [];
                this.warehouseItemsLoading = false;
                this.warehouseItemsMessage = 'Warehouse items could not be loaded. Refresh after the shipment intake service is available.';
                this.syncFirstCargoLineValidation();
            }
        });
    }

    private applyWarehouseItemOptions(items: ShipmentIntakeWarehouseItemOption[], clearMissingSelections: boolean): void {
        this.warehouseUomCache = new WeakMap();
        this.warehouseItemOptions = [...items].sort((left, right) =>
            (left.itemName || left.itemNumber || '').localeCompare(right.itemName || right.itemNumber || '')
        );
        if (clearMissingSelections) {
            this.shipment.payloadItemList.forEach((line, index) => {
                if (this.isWarehouseItemLine(line)
                    && line.inventoryItemId != null
                    && !this.warehouseItemOptions.some(item => item.inventoryItemId === line.inventoryItemId)) {
                    this.clearWarehouseItemSnapshot(line, true);
                    this.cargoTotalWeights[index] = 0;
                }
            });
        }
        this.warehouseItemsLoading = false;
        this.warehouseItemsMessage = this.warehouseItemOptions.length > 0
            ? ''
            : 'No active warehouse items match the selected CRM client and account.';
        this.syncFirstCargoLineValidation();
    }

    private findKilogramWeightUnit(): any {
        return this.weightUnitList.find(unit => this.matchesAny(unit?.shortName, ['kg', 'kgs']))
            ?? this.weightUnitList.find(unit => String(unit?.fullName ?? '').toLowerCase().includes('kilogram'));
    }

    removeCargoLine(index: number): void {
        if (this.shipment.payloadItemList.length <= 1) return;
        this.shipment.payloadItemList.splice(index, 1);
        this.cargoTotalWeights.splice(index, 1);
        this.syncFirstCargoLineValidation();
        this.refreshIntakePreviews();
    }

    addRouteStop(): void {
        this.routeStops.push({ stopType: ShipmentWaypointType.DROPOFF, scheduledArrival: null, scheduledDeparture: null });
        this.refreshIntakePreviews();
    }

    onStopLocationSelected(stop: RouteStopDraft): void {
        this.pinLocation(stop.location);
        if (stop.location?.defaultContact) {
            stop.contact = stop.location.defaultContact;
            this.addCustomerOption(stop.contact);
        }
        this.refreshIntakePreviews();
    }

    registerStopContact(index: number): void {
        this.registerContact('stop', index);
    }

    removeRouteStop(index: number): void {
        this.routeStops.splice(index, 1);
        this.refreshIntakePreviews();
    }

    addWatcher(): void {
        this.addWatcherContact(this.selectedWatcher);
        this.selectedWatcher = undefined;
        this.refreshIntakePreviews();
    }

    addWatcherContact(contact?: Customer): void {
        if (!contact || this.watcherList.some(watcher => watcher.id === contact.id)) return;
        this.addCustomerOption(contact);
        this.watcherList = [...this.watcherList, contact];
    }

    private intakeStopContacts(): Customer[] {
        const contacts = [
            ...this.watcherList,
            ...this.routeStops.map(stop => stop.contact).filter((contact): contact is Customer => !!contact?.id)
        ];
        return contacts.filter((contact, index) =>
            contacts.findIndex(candidate => candidate.id === contact.id) === index);
    }

    removeWatcher(index: number): void {
        this.watcherList.splice(index, 1);
        this.watcherList = [...this.watcherList];
        this.refreshIntakePreviews();
    }

    isExecutionLocked(): boolean {
        return false;
    }

    hasInvalidDeliveryWindow(): boolean {
        return !!this.pickupDate
            && !!this.deliveryWindowEnd
            && this.deliveryWindowEnd.valueOf() < this.pickupDate.valueOf();
    }

    canSubmitIntake(): boolean {
        return !this.createShipmentForm?.invalid
            && this.canSaveShipment
            && !!this.intakeReadiness?.ready
            && !this.shipmentCreationInProgress;
    }

    private locationCoordinateValidationIssues(): string[] {
        const issues: string[] = [];
        if (this.shipment.sourceLocation && !this.hasValidLocationCoordinates(this.shipment.sourceLocation)) {
            issues.push('Pickup location must have a valid latitude and longitude.');
        }
        if (this.shipment.destinationLocation && !this.hasValidLocationCoordinates(this.shipment.destinationLocation)) {
            issues.push('Drop-off location must have a valid latitude and longitude.');
        }
        this.routeStops.forEach((stop, index) => {
            if (stop.location && !this.hasValidLocationCoordinates(stop.location)) {
                issues.push(`Intermediate stop ${index + 1} must have a valid latitude and longitude.`);
            }
        });
        return issues;
    }

    private hasValidLocationCoordinates(location?: Location): boolean {
        const latitudeValue = location?.location?.latitude;
        const longitudeValue = location?.location?.longitude;
        if (latitudeValue == null || longitudeValue == null) {
            return false;
        }
        const latitude = Number(latitudeValue);
        const longitude = Number(longitudeValue);
        return Number.isFinite(latitude) && Number.isFinite(longitude)
            && latitude >= -90 && latitude <= 90
            && longitude >= -180 && longitude <= 180;
    }

    private intakeSaveErrorMessage(error: any, validationIssues?: string[]): string {
        if (error?.status >= 500) {
            return 'The shipment could not be created because the server returned an unexpected error. Please try again or contact support if it continues.';
        }
        if (validationIssues?.length) {
            return validationIssues.join(', ');
        }
        const responseMessage = error?.error?.message || error?.error?.error || error?.message;
        if (responseMessage && !String(responseMessage).startsWith('Http failure response')) {
            return String(responseMessage);
        }
        return 'Review the required intake fields and try again.';
    }

    private setSubmissionIssues(issues: unknown, fallback: string): string[] {
        const sourceIssues = Array.isArray(issues) ? issues : issues == null ? [] : [issues];
        const normalizedIssues = sourceIssues
            .filter((issue): issue is string => typeof issue === 'string')
            .map(issue => issue.trim())
            .filter(Boolean);
        this.submissionIssues = [...new Set(normalizedIssues.length ? normalizedIssues : [fallback])];
        return this.submissionIssues;
    }

    readinessPercent(): number {
        const checks = [
            this.intakeReadiness?.partiesReady,
            this.intakeReadiness?.routeReady,
            this.intakeReadiness?.cargoReady,
            this.intakeReadiness?.equipmentReady,
            ...(this.canViewShipmentPricing() ? [this.intakeReadiness?.pricingReady] : [])
        ];
        return Math.round(checks.filter(Boolean).length / checks.length * 100);
    }

    formatDurationHours(seconds?: number): string {
        if (!seconds) return 'Pending';
        return `${(seconds / 3600).toFixed(1)} hrs`;
    }

    documentChecklist(): Array<{ label: string; status: string; severity: string }> {
        const routeType = this.createShipmentForm?.get('routeTypeDropdown')?.value ?? 'DOMESTIC';
        const documentCount = this.shipmentDocumentCount();
        const pricingReady = !this.requiresManualOverrideReason() || !!this.manualOverrideReason?.trim();
        const required = [
            { label: 'Commercial buying basis', status: pricingReady ? 'Ready' : 'Pending', severity: pricingReady ? 'success' : 'warn' },
            { label: 'Pickup and delivery contacts', status: this.selectedSenderContact && this.selectedReceiverContact ? 'Selected' : 'Creator will be used', severity: 'info' },
            { label: 'Cargo readiness evidence', status: documentCount > 0 ? `${documentCount} attached` : 'Pending upload', severity: documentCount > 0 ? 'success' : 'info' }
        ];
        if (routeType === 'CROSS_BORDER') {
            required.push({ label: 'Cross-border compliance pack', status: 'Required before dispatch', severity: 'danger' });
        }
        return required;
    }

    onShipmentDocumentsSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const files = Array.from(input.files ?? []);
        if (!files.length) {
            return;
        }
        const existingKeys = new Set(this.shipmentDocumentQueue.map(document => this.fileKey(document.file)));
        const newDocuments = files
            .filter(file => !existingKeys.has(this.fileKey(file)))
            .map(file => ({ file, progress: 0, state: 'queued' }));
        this.shipmentDocumentQueue = [...this.shipmentDocumentQueue, ...newDocuments];
        input.value = '';
        this.refreshIntakePreviews();
    }

    removeQueuedShipmentDocument(index: number): void {
        this.shipmentDocumentQueue.splice(index, 1);
        this.shipmentDocumentQueue = [...this.shipmentDocumentQueue];
        this.refreshIntakePreviews();
    }

    shipmentLevelAttachments(): ShipmentDeliveryAttachment[] {
        return (this.shipment.filesList ?? []).filter(file => !file.deliveryId);
    }

    shipmentDocumentCount(): number {
        return this.shipmentLevelAttachments().length + this.shipmentDocumentQueue.length;
    }

    formatFileSize(bytes?: number): string {
        if (!bytes) {
            return '0 KB';
        }
        if (bytes < 1024 * 1024) {
            return `${Math.max(1, Math.round(bytes / 1024))} KB`;
        }
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    loadContractOptions(event?: any): void {
        if (this.contractOptionsLoading) return;
        this.contractOptionsLoading = true;
        const rows = event?.rows ?? 50;
        const page = event?.first && rows ? Math.floor(event.first / rows) : 0;
        const companyId = this.owningCompanyId;
        const buyerCompanyId = this.resolveBuyingCompanyId();
        const crmClientId = this.shipment.crmClientId;
        // CRM commercial parties are the source of truth for newly-created
        // shipments.  A CRM client is not guaranteed to have a legacy
        // Customer/Company link, so querying by the latter hides otherwise
        // valid CRM-linked contracts and their rate cards.
        this.contractService.getAllContracts(
            page,
            rows,
            crmClientId ? undefined : companyId,
            crmClientId ? undefined : buyerCompanyId,
            crmClientId
        ).subscribe({
            next: response => {
                if (crmClientId !== this.shipment.crmClientId || buyerCompanyId !== this.resolveBuyingCompanyId()) return;
                const options = (response.items ?? []).filter(contract =>
                    !companyId || !crmClientId || contract.firstParty?.id === companyId
                );
                this.contractOptions = this.mergeContracts(this.contractOptions, options);
                if (this.pricingContractId && !this.selectedPricingContract) {
                    this.selectedPricingContract = this.contractOptions.find(contract => contract.contractID === this.pricingContractId);
                }
            },
            error: () => this.contractOptionsLoading = false,
            complete: () => this.contractOptionsLoading = false
        });
    }

    loadSupplierClients(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId || this.supplierClientsLoading) return;
        this.supplierClientsLoading = true;
        this.crmService.getClients(companyId, ['SUPPLIER', 'TRANSPORTER']).subscribe({
            next: clients => {
                this.supplierClients.set((clients ?? []).map(client => ({
                    ...client,
                    displayLabel: `${client.companyName}${client.clientNumber ? ' | ' + client.clientNumber : ''}`
                })));
                if (this.pricingPreview?.supplierCrmClientId && !this.selectedSupplierClient) {
                    this.selectedSupplierClient = this.supplierClients().find(client => client.id === this.pricingPreview?.supplierCrmClientId);
                }
            },
            error: () => this.supplierClients.set([]),
            complete: () => this.supplierClientsLoading = false
        });
    }

    onBuyingCostSourceSelected(source: string): void {
        this.buyingCostSource = source;
        this.buyingPriceEditedManually = source === 'MANUAL';
        this.lastRecommendedBuyingPrice = undefined;
        this.clearOperationalCostFields();
        if (!this.isSupplierBuyingSource()) {
            this.selectedSupplierClient = undefined;
            this.selectedSupplierContract = undefined;
            this.selectedSupplierRateCard = undefined;
            this.supplierContractOptions = [];
            this.supplierRateCardOptions = [];
        } else {
            this.loadSupplierClients();
        }
        if (!this.isSisterFleetBuyingSource()) {
            this.selectedSisterCompany = undefined;
        }
        this.refreshIntakePreviews();
    }

    onSisterCompanySelected(company?: Company): void {
        this.selectedSisterCompany = company;
        this.buyingPriceEditedManually = false;
        this.clearOperationalCostFields();
        this.refreshIntakePreviews();
    }

    onSupplierClientSelected(client?: CrmClient & { displayLabel?: string }): void {
        this.selectedSupplierClient = client;
        this.selectedSupplierContract = undefined;
        this.selectedSupplierRateCard = undefined;
        this.supplierContractOptions = [];
        this.supplierRateCardOptions = [];
        this.buyingPriceEditedManually = false;
        this.clearOperationalCostFields();
        if (client) {
            this.loadSupplierContractOptions();
        }
        this.refreshIntakePreviews();
    }

    loadSupplierContractOptions(event?: any): void {
        if (this.supplierContractOptionsLoading || !this.selectedSupplierClient?.id) return;
        const rows = event?.rows ?? 50;
        const page = event?.first && rows ? Math.floor(event.first / rows) : 0;
        const supplierCompanyId = this.selectedSupplierClient.linkedCompanyId;
        const buyingCompanyId = this.sessionStorageService.getCompany()?.id;
        this.supplierContractOptionsLoading = true;
        this.contractService.getAllContracts(page, rows, supplierCompanyId, buyingCompanyId, this.selectedSupplierClient.id).subscribe({
            next: response => {
                const options = response.items ?? [];
                this.supplierContractOptions = this.mergeContracts(this.supplierContractOptions, options);
                if (this.pricingPreview?.supplierContractId && !this.selectedSupplierContract) {
                    this.selectedSupplierContract = this.supplierContractOptions.find(contract => contract.contractID === this.pricingPreview?.supplierContractId);
                }
            },
            error: () => this.supplierContractOptionsLoading = false,
            complete: () => this.supplierContractOptionsLoading = false
        });
    }

    onSupplierContractSelected(contract?: Contracts): void {
        this.selectedSupplierContract = contract;
        this.selectedSupplierRateCard = undefined;
        this.supplierRateCardOptions = [];
        this.buyingPriceEditedManually = false;
        this.clearOperationalCostFields();
        if (contract) {
            this.loadSupplierRateCardOptions();
        }
        this.refreshIntakePreviews();
    }

    loadSupplierRateCardOptions(): void {
        if (this.supplierRateCardOptionsLoading || !this.selectedSupplierContract?.contractID) return;
        const revision = this.activeRevision(this.selectedSupplierContract);
        if (!revision?.revisionID) return;
        this.supplierRateCardOptionsLoading = true;
        this.contractService.getDeliveryRateCardsByRevisionId(this.selectedSupplierContract.contractID, revision.revisionID).subscribe({
            next: rateCards => {
                this.supplierRateCardOptions = (rateCards ?? []).map(rateCard => this.toRateCardOption(rateCard));
                if (this.pricingPreview?.supplierRateCardId && !this.selectedSupplierRateCard) {
                    this.selectedSupplierRateCard = this.supplierRateCardOptions.find(rateCard => rateCard.rateId === this.pricingPreview?.supplierRateCardId);
                }
            },
            error: () => this.supplierRateCardOptionsLoading = false,
            complete: () => this.supplierRateCardOptionsLoading = false
        });
    }

    onSupplierRateCardSelected(rateCard?: DeliveryRateCard): void {
        this.selectedSupplierRateCard = rateCard ? this.toRateCardOption(rateCard) : undefined;
        this.buyingPriceEditedManually = false;
        this.clearOperationalCostFields();
        this.refreshIntakePreviews();
    }

    onPricingContractSelected(contract?: Contracts): void {
        if (this.pricingRateCardId) this.shipment.sellingPrice = undefined;
        this.selectedPricingContract = contract;
        this.pricingContractId = contract?.contractID;
        this.pricingContractRevisionId = this.activeRevision(contract)?.revisionID;
        this.selectedPricingRateCard = undefined;
        this.pricingRateCardId = undefined;
        this.rateCardOptions = [];
        this.rateCardOptionsLoading = false;
        if (contract) {
            this.rateSource = 'RATE_CARD';
            this.refreshContractPricingDecision(true);
        }
        this.refreshIntakePreviews();
    }

    onPricingRevisionSelected(revisionId?: number): void {
        if (this.pricingRateCardId) this.shipment.sellingPrice = undefined;
        this.pricingContractRevisionId = revisionId;
        this.selectedPricingRateCard = undefined;
        this.pricingRateCardId = undefined;
        this.rateCardOptions = [];
        this.rateCardOptionsLoading = false;
        if (revisionId) this.refreshContractPricingDecision(true);
        this.refreshIntakePreviews();
    }

    loadRateCardOptions(): void {
        if (this.rateCardOptionsLoading || !this.selectedPricingContract?.contractID) return;
        const revision = this.selectedPricingContract.revisions?.find(row => row.revisionID === this.pricingContractRevisionId);
        if (!revision?.revisionID) return;
        const contractId = this.selectedPricingContract.contractID;
        const revisionId = revision.revisionID;
        this.rateCardOptionsLoading = true;
        this.contractService.getDeliveryRateCardsByRevisionId(contractId, revisionId).subscribe({
            next: rateCards => {
                if (this.selectedPricingContract?.contractID !== contractId || this.pricingContractRevisionId !== revisionId) return;
                this.rateCardOptions = (rateCards ?? []).map(rateCard => this.toRateCardOption(rateCard));
                if (this.pricingRateCardId && !this.selectedPricingRateCard) {
                    this.selectedPricingRateCard = this.rateCardOptions.find(rateCard => rateCard.rateId === this.pricingRateCardId);
                }
            },
            error: () => this.rateCardOptionsLoading = false,
            complete: () => this.rateCardOptionsLoading = false
        });
    }

    onPricingRateCardSelected(rateCard?: DeliveryRateCard): void {
        if (!rateCard && this.pricingRateCardId) this.shipment.sellingPrice = undefined;
        this.selectedPricingRateCard = rateCard ? this.toRateCardOption(rateCard) : undefined;
        this.pricingRateCardId = rateCard?.rateId;
        this.pricingContractRevisionId = rateCard?.contractRevisionId ?? this.pricingContractRevisionId;
        if (rateCard) {
            this.rateSource = 'RATE_CARD';
            this.shipment.sellingPrice = rateCard.amount;
            this.manualOverrideReason = '';
            this.refreshContractPricingDecision(true);
        }
        this.refreshIntakePreviews();
    }

    refreshIntakePreviews(): void {
        this.refreshContractPricingDecision();
        const request = this.toIntakeRequest();
        const pricingSequence = ++this.pricingPreviewSequence;
        this.previewInProgress = true;
        this.shipmentIntakeService.readinessCheck(request).subscribe({
            next: readiness => this.intakeReadiness = readiness,
            // A partially completed form is a normal state. Do not let a
            // transient readiness failure interrupt source switching or leave
            // the preview spinner running.
            error: () => this.previewInProgress = false,
            complete: () => this.previewInProgress = false
        });
        if (request.sourceLocationId && request.destinationLocationId) {
            this.shipmentIntakeService.routePreview(request).subscribe({
                next: preview => this.routePreview = preview,
                error: () => {}
            });
            this.shipmentIntakeService.ratePreview(request).subscribe({
                next: pricing => {
                    if (pricingSequence !== this.pricingPreviewSequence) return;
                    this.pricingPreview = pricing;
                    this.pricingWarnings = pricing.warnings ?? [];
                    this.lastRecommendedBuyingPrice = pricing.recommendedBuyingPrice ?? undefined;
                    if (!this.buyingPriceEditedManually && this.buyingCostSource !== 'MANUAL') {
                        this.applyRecommendedOperationalCost(pricing);
                    }
                },
                error: () => {}
            });
        }
    }

    onOperationalCostChanged(value: number | null | undefined): void {
        if (this.usesInternalCostField()) {
            this.shipment.pricingInternalCostAmount = value ?? undefined;
            this.shipment.buyingPrice = undefined;
        } else {
            this.shipment.buyingPrice = value ?? undefined;
            this.shipment.pricingInternalCostAmount = undefined;
        }
        if (this.buyingCostSource === 'MANUAL') {
            this.buyingPriceEditedManually = true;
        } else if (this.lastRecommendedBuyingPrice != null && value != null) {
            this.buyingPriceEditedManually = Math.abs(Number(value) - Number(this.lastRecommendedBuyingPrice)) > 0.01;
        } else {
            this.buyingPriceEditedManually = value != null;
        }
        this.refreshIntakePreviews();
    }

    onSellingRateSourceSelected(source: string): void {
        this.rateSource = source;
        this.rateMatchKey = '';
        if (source === 'MANUAL') {
            this.rateMatchSequence++;
            this.selectedPricingContract = undefined;
            this.selectedPricingRateCard = undefined;
            this.pricingContractId = undefined;
            this.pricingContractRevisionId = undefined;
            this.pricingRateCardId = undefined;
        } else {
            this.loadContractOptions();
            this.refreshContractPricingDecision();
        }
        this.refreshIntakePreviews();
    }

    internalCostCurrencyCode(): string {
        return this.pricingPreview?.internalCostCurrencyCode || this.currencyCode || 'SAR';
    }

    usesInternalCostField(): boolean {
        return this.buyingCostSource === 'OWNED' || this.buyingCostSource === 'SISTER_OWNED';
    }

    operationalCostFieldLabel(): string {
        return this.usesInternalCostField() ? 'Internal Cost' : 'Buying Price';
    }

    recommendedCostLabel(): string {
        return this.usesInternalCostField() ? 'Recommended Internal Cost' : 'Recommended Buying';
    }

    operationalCostAmount(): number | undefined {
        return this.usesInternalCostField()
            ? this.shipment.pricingInternalCostAmount ?? undefined
            : this.shipment.buyingPrice ?? undefined;
    }

    operationalCostMarginPreview(): number {
        return (this.shipment.sellingPrice || 0) - (this.operationalCostAmount() || 0);
    }

    internalCostStatusLabel(): string {
        if (this.pricingPreview?.internalCostMatched) {
            return this.pricingPreview.internalCostMatchType || 'MATCHED';
        }
        return this.pricingPreview?.internalCostMatchType || 'NO_MATCH';
    }

    buyingSourceBadgeLabel(): string {
        const source = this.pricingPreview?.buyingPriceSource;
        if (this.buyingPriceEditedManually || this.buyingCostSource === 'MANUAL') return 'Manual';
        if (source === 'INTERNAL_TRANSPORT_COST') return 'Internal cost';
        if (source === 'SISTER_INTERNAL_TRANSPORT_COST') return 'Sister internal cost';
        if (source === 'SUPPLIER_DELIVERY_RATE_CARD') return 'Supplier rate card';
        if (source === 'NO_MATCH') return 'No match';
        return this.buyingCostSource === 'OWNED' ? 'Internal cost' : 'No match';
    }

    sellingSourceBadgeLabel(): string {
        if (this.rateSource === 'MANUAL') return 'Manual';
        if (this.pricingRateCardId) return this.pickupDate ? 'Rate card' : 'Rate card preview';
        if (!this.pickupDate || !this.shipment.sourceLocation?.id || !this.shipment.destinationLocation?.id
            || !this.shipment.vehicleParaPack?.vehicleType?.id) return 'Rate card pending';
        return this.pricingWarnings.length ? 'No rate card match' : 'Rate card pending';
    }

    sourceBadgeClass(label: string): string {
        const normalized = label.toLowerCase();
        if (normalized.includes('no match')) return 'source-badge source-badge-danger';
        if (normalized.includes('manual')) return 'source-badge source-badge-manual';
        if (normalized.includes('preview')) return 'source-badge source-badge-info';
        if (normalized.includes('supplier')) return 'source-badge source-badge-info';
        return 'source-badge source-badge-success';
    }

    buyingWarning(): string | undefined {
        return this.pricingPreview?.pricingWarning || this.pricingWarnings?.find(warning => warning?.toLowerCase().includes('buying'));
    }

    isSupplierBuyingSource(): boolean {
        return this.buyingCostSource === 'FREELANCE' || this.buyingCostSource === 'RENTAL';
    }

    isSisterFleetBuyingSource(): boolean {
        return this.buyingCostSource === 'SISTER_OWNED';
    }

    requiresManualOverrideReason(): boolean {
        return this.buyingCostSource === 'MANUAL' || this.buyingPriceEditedManually;
    }

    private clearOperationalCostFields(): void {
        this.shipment.buyingPrice = undefined;
        this.shipment.pricingInternalCostAmount = undefined;
    }

    private applyRecommendedOperationalCost(pricing: ShipmentIntakePricing): void {
        if (this.usesInternalCostField()) {
            this.shipment.pricingInternalCostAmount = pricing.internalCostAmount
                ?? pricing.recommendedBuyingPrice
                ?? undefined;
            this.shipment.buyingPrice = undefined;
            return;
        }
        this.shipment.buyingPrice = pricing.recommendedBuyingPrice ?? pricing.buyingPrice ?? undefined;
        this.shipment.pricingInternalCostAmount = undefined;
    }

    private prepareCargoLines(): void {
        const palletUom = this.findDefaultCargoUom();
        const tonUnit = this.findDefaultCargoWeightUnit();
        this.shipment.payloadItemList.forEach((line, index) => {
            line.packingListIndex = index + 1;
            line.lineSource = this.isContainerLine(line) ? 'CONTAINER'
                : this.isWarehouseItemLine(line) ? 'WAREHOUSE_ITEM' : 'GENERAL_CARGO';
            if (this.isContainerLine(line)) {
                return;
            }
            line.itemQuantity = line.itemQuantity ?? new QuantityView();
            line.itemQuantity.uom = line.itemQuantity.uom ?? palletUom;
            line.weightUnit = line.weightUnit ?? tonUnit;
            const quantity = line.itemQuantity.quantity ?? 0;
            line.itemWeight = quantity > 0 ? (this.cargoTotalWeights[index] ?? 0) / quantity : 0;
        });
        this.totalWeight = this.cargoTotalWeights.reduce((sum, weight) => sum + (weight ?? 0), 0);
    }

    private findDefaultCargoUom(): any {
        return this.uomList.find(uom => this.matchesAny(uom?.name, ['pallet', 'pallets']))
            ?? this.uomList.find(uom => this.matchesAny(uom?.shortName, ['plt', 'plt.', 'pallet', 'pallets']))
            ?? this.uomList.find(uom => this.matchesAny(uom?.name, ['unit', 'units']))
            ?? this.uomList[0];
    }

    private findEachUom(): any {
        return this.uomList.find(uom => this.matchesAny(uom?.name, ['each', 'eachs', 'eaches']))
            ?? this.uomList.find(uom => this.matchesAny(uom?.shortName, ['ea', 'each']));
    }

    private findDefaultCargoWeightUnit(): any {
        return this.weightUnitList.find(weight => this.matchesAny(weight?.shortName, ['ton', 'tons', 't', 'mt']))
            ?? this.weightUnitList.find(weight => this.matchesAny(weight?.fullName, ['metric ton', 'metric tons', 'ton', 'tons', 'tonne', 'tonnes']))
            ?? this.weightUnitList[0];
    }

    private matchesAny(value: string | undefined, candidates: string[]): boolean {
        if (!value) return false;
        const normalized = value.trim().toLowerCase();
        return candidates.includes(normalized);
    }

    private syncPartiesFromLocations(type?: 'source' | 'destination'): void {
        if (this.shipment.sourceLocation) {
            if (!type || type === 'source') {
                this.selectedSenderCompany = this.shipment.sourceLocation.company;
                this.selectedSenderContact = this.shipment.sourceLocation.defaultContact;
                this.addCompanyOption(this.selectedSenderCompany);
                this.addCustomerOption(this.selectedSenderContact);
            }
        }
        if (this.shipment.destinationLocation) {
            if (!type || type === 'destination') {
                this.selectedReceiverCompany = this.shipment.destinationLocation.company;
                this.selectedReceiverContact = this.shipment.destinationLocation.defaultContact;
                this.addCompanyOption(this.selectedReceiverCompany);
                this.addCustomerOption(this.selectedReceiverContact);
            }
        }
    }

    private syncPartiesFromLoadedShipment(): void {
        this.selectedSenderCompany = this.shipment.nominationPack?.sender?.client ?? this.shipment.sourceLocation?.company;
        this.selectedSenderContact = this.shipment.nominationPack?.sender?.nominee ?? this.shipment.sourceLocation?.defaultContact;
        this.selectedReceiverCompany = this.shipment.nominationPack?.receiver?.client ?? this.shipment.destinationLocation?.company;
        this.selectedReceiverContact = this.shipment.nominationPack?.receiver?.nominee ?? this.shipment.destinationLocation?.defaultContact;
        this.watcherList = this.shipment.nominationPack?.watchers?.map(watcher => watcher.nominee).filter(Boolean) ?? [];
        [this.selectedSenderCompany, this.selectedReceiverCompany].forEach(company => this.addCompanyOption(company));
        [this.selectedSenderContact, this.selectedReceiverContact, ...this.watcherList].forEach(contact => this.addCustomerOption(contact));
    }

    private addCompanyOption(company?: Company): void {
        if (!company?.id || this.companyList().some(option => option.id === company.id)) return;
        this.companyList.set([company, ...this.companyList()]);
    }

    private addCustomerOption(customer?: Customer): void {
        if (!customer?.id || this.partyCustomerPins().some(option => option.id === customer.id)) return;
        this.partyCustomerPins.set([...this.partyCustomerPins(), customer]);
    }

    private configureCommercialScope(currentCompany?: Company): void {
        const companyId = currentCompany?.id ?? this.sessionStorageService.getCompany()?.id;
        this.sisterCompanyScope = resolveSisterCompanyScope(this.sessionStorageService.getTenantAccessSummary(), companyId);
        if (this.pricingPreview?.resourceCompanyId && !this.selectedSisterCompany) {
            this.selectedSisterCompany = this.sisterCompanyScope?.sisterCompanies
                .find(company => company.id === this.pricingPreview?.resourceCompanyId);
        }
        this.loadSupplierClients();
    }

    private mergeContracts(existing: Contracts[], incoming: Contracts[]): Contracts[] {
        const merged = [...existing];
        incoming.forEach(contract => {
            if (!merged.some(option => option.contractID === contract.contractID)) {
                merged.push(contract);
            }
        });
        return merged;
    }

    private activeRevision(contract?: Contracts) {
        const pickupMillis = this.pickupDate?.valueOf() ?? Date.now();
        return [...(contract?.revisions ?? [])]
            .filter(revision => String(revision.status) === 'ACTIVE')
            .filter(revision => {
                const start = revision.effectiveStartDate ? new Date(revision.effectiveStartDate).getTime() : 0;
                const end = revision.endDate ? new Date(revision.endDate).getTime() : Number.MAX_SAFE_INTEGER;
                return start <= pickupMillis && end >= pickupMillis;
            })
            .sort((left, right) => Number(right.revisionID ?? 0) - Number(left.revisionID ?? 0))[0]
            ?? contract?.revisions?.[0];
    }

    private resolveBuyingCompanyId(): number | undefined {
        if (this.commercialPartyReference?.source === 'CRM' && this.shipment.crmClientId) return undefined;
        return this.commercialPartyReference?.legacyCompanyId
            ?? this.shipment.customer?.company?.id
            ?? this.selectedPricingContract?.secondParty?.id
            ?? this.selectedReceiverCompany?.id
            ?? this.shipment.destinationLocation?.company?.id;
    }

    private toContractPricingDecisionRequest(): ContractPricingDecisionRequest {
        return {
            sellingCompanyId: this.owningCompanyId,
            buyingCompanyId: this.resolveBuyingCompanyId(),
            crmClientId: this.shipment.crmClientId,
            crmAccountId: this.shipment.crmAccountId,
            sourceLocationId: this.shipment.sourceLocation?.id,
            destinationLocationId: this.shipment.destinationLocation?.id,
            // shortcut: Today's date previews a rate before pickup is chosen; pickup changes rerun the match.
            pickupWindowStart: this.pickupDate?.valueOf() ?? new Date().setHours(12, 0, 0, 0),
            vehicleTypeId: this.shipment.vehicleParaPack?.vehicleType?.id,
            vehicleConfigId: this.shipment.vehicleParaPack?.vehicleConfig?.configId,
            vehicleCapacityId: this.shipment.vehicleParaPack?.vehicleCapacity?.capacityId,
            goodsInsurance: false,
            portPermit: false,
            contractId: this.pricingContractId,
            contractRevisionId: this.pricingContractRevisionId,
            rateCardId: this.pricingRateCardId,
            currencyCode: this.currencyCode
        };
    }

    private toRateCardOption(rateCard: DeliveryRateCard): DeliveryRateCard & { displayLabel?: string } {
        return {
            ...rateCard,
            displayLabel: `${rateCard.sourceCity || 'Source'} -> ${rateCard.destinationCity || 'Destination'} (${rateCard.amount ?? 0} ${this.currencyCode})`
        };
    }

    private uploadQueuedShipmentDocuments(shipmentId: number): Promise<ShipmentDeliveryAttachment[]> {
        const queuedDocuments = [...this.shipmentDocumentQueue];
        if (!queuedDocuments.length) {
            return Promise.resolve([]);
        }
        return Promise.all(queuedDocuments.map(document => this.uploadQueuedShipmentDocument(shipmentId, document)))
            .then(attachments => {
                this.shipment.filesList = [...(this.shipment.filesList ?? []), ...attachments];
                this.shipmentDocumentQueue = [];
                this.refreshIntakePreviews();
                return attachments;
            });
    }

    private uploadQueuedShipmentDocument(shipmentId: number, document: ShipmentDocumentDraft): Promise<ShipmentDeliveryAttachment> {
        return new Promise((resolve, reject) => {
            let completed = false;
            const progressCallback = (progress: ShipmentUploadProgress) => {
                document.progress = progress.progress;
                document.state = progress.state;
                this.shipmentDocumentQueue = [...this.shipmentDocumentQueue];
                if (progress.state === 'error' && !completed) {
                    completed = true;
                    reject(new Error(`Failed to upload ${document.file.name}`));
                }
            };
            this.googleCloudStorageService.uploadShipmentFile(
                document.file,
                document.file.name,
                document.file.type || 'application/octet-stream',
                false,
                0,
                shipmentId,
                undefined,
                undefined,
                attachment => {
                    if (!completed) {
                        completed = true;
                        resolve(attachment);
                    }
                },
                progressCallback
            );
        });
    }

    private toIntakeAttachments() {
        return [
            ...this.shipmentLevelAttachments().map(file => ({
                attachmentId: file.attachmentId,
                fileName: file.fileName,
                fileUrl: file.fileUrl,
                fileSize: file.fileSize,
                fileType: file.fileType
            })),
            ...this.shipmentDocumentQueue.map(document => ({
                fileName: document.file.name,
                fileSize: document.file.size,
                fileType: document.file.type || 'application/octet-stream'
            }))
        ];
    }

    private fileKey(file: File): string {
        return `${file.name}:${file.size}:${file.lastModified}`;
    }

    private toIntakeRequest(prepareCargo: boolean = true): ShipmentIntakeRequest {
        if (prepareCargo) {
            this.prepareCargoLines();
        }
        const currentCompany = this.sessionStorageService.getCompany();
        const currentCustomer = this.sessionStorageService.getCustomer();
        const sender = this.shipment.sourceLocation;
        const receiver = this.shipment.destinationLocation;
        return {
            shipmentId: undefined,
            warehouseFlowDirection: this.shipment.warehouseFlowDirection ?? 'OUTBOUND',
            companyId: this.shipment.company?.id,
            customerId: this.shipment.customer?.id ?? currentCustomer?.id,
            commercialPartySource: this.commercialPartyReference.source === 'CRM' ? 'CRM' : 'LEGACY',
            crmClientId: this.shipment.crmClientId,
            crmAccountId: this.shipment.crmAccountId,
            crmClientContactId: this.shipment.crmClientContactId,
			paymentTermId: this.paymentTermId,
            legacyCompanyId: this.commercialPartyReference.source === 'LEGACY'
                ? this.commercialPartyReference.legacyCompanyId ?? undefined : undefined,
            legacyCustomerId: this.commercialPartyReference.source === 'LEGACY'
                ? this.commercialPartyReference.legacyCustomerId ?? undefined : undefined,
            owningCompanyId: this.shipment.company?.id !== currentCompany?.id ? currentCompany?.id : undefined,
            owningCompanyContactId: this.shipment.company?.id !== currentCompany?.id ? currentCustomer?.id : undefined,
            logisticsProjectId: this.shipment.logisticsProjectId,
            countryOfOrderId: this.shipment.countryOfOrder?.id,
            sourceLocationId: this.shipment.sourceLocation?.id,
            destinationLocationId: this.shipment.destinationLocation?.id,
            pickupWindowStart: this.pickupDate?.valueOf(),
            deliveryWindowEnd: this.deliveryWindowEnd?.valueOf(),
            requestedTruckCount: this.shipment.quantityRequired,
            routeType: this.createShipmentForm?.get('routeTypeDropdown')?.value ?? this.shipment.routeType ?? 'DOMESTIC',
            serviceCategory: this.createShipmentForm?.get('serviceCategoryDropdown')?.value ?? this.shipment.serviceCategory ?? 'FULL_SERVICE',
            vehicleTypeId: this.shipment.vehicleParaPack?.vehicleType?.id,
            vehicleConfigId: this.shipment.vehicleParaPack?.vehicleConfig?.configId,
            vehicleCapacityId: this.shipment.vehicleParaPack?.vehicleCapacity?.capacityId,
            externalReference: this.shipmentReference.trim() || undefined,
            notes: this.shipmentNotes,
            priority: this.priority,
            parties: [
                {
                    role: 'SENDER',
                    companyId: this.selectedSenderCompany?.id ?? sender?.company?.id,
                    contactId: this.selectedSenderContact?.id ?? sender?.defaultContact?.id
                },
                {
                    role: 'RECEIVER',
                    companyId: this.selectedReceiverCompany?.id ?? receiver?.company?.id,
                    contactId: this.selectedReceiverContact?.id ?? receiver?.defaultContact?.id
                },
                ...this.intakeStopContacts().map(watcher => ({
                    role: 'WATCHER' as const,
                    companyId: watcher.company?.id,
                    contactId: watcher.id
                }))
            ],
            stops: this.routeStops.map((stop, index) => ({
                stopId: stop.waypointId,
                stopNumber: index + 2,
                stopType: stop.stopType,
                locationId: stop.location?.id,
                companyId: stop.contact?.company?.id ?? stop.location?.company?.id,
                contactId: stop.contact?.id,
                scheduledArrival: stop.scheduledArrival?.valueOf(),
                scheduledDeparture: stop.scheduledDeparture?.valueOf(),
                remarks: stop.remarks
            })),
            lines: this.shipment.payloadItemList.map((line, index) => ({
                lineId: line.shipmentLineId,
                lineNumber: index + 1,
                lineSource: this.isContainerLine(line) ? 'CONTAINER'
                    : this.isWarehouseItemLine(line) ? 'WAREHOUSE_ITEM' : 'GENERAL_CARGO',
                inventoryItemId: line.inventoryItemId,
                warehouseItemNumber: line.warehouseItemNumber,
                skuNumber: line.skuNumber,
                customerItemNumber: line.customerItemNumber,
                warehouseItemName: line.warehouseItemName,
                trackingMode: line.trackingMode,
                sourceWarehouseId: this.shipment.warehouseFlowDirection === 'INBOUND'
                    ? undefined : line.sourceWarehouseId,
                conditionId: line.conditionId,
                cargoTypeId: line.itemType?.typeId != null ? Number(line.itemType.typeId) : undefined,
                description: line.itemDesciption || line.itemType?.name || `Cargo line ${index + 1}`,
                quantity: this.isWarehouseItemLine(line) && line.tenantUomId != null
                    ? undefined : line.itemQuantity?.quantity,
                enteredQuantity: this.isWarehouseItemLine(line) && line.tenantUomId != null
                    ? line.itemQuantity?.quantity : undefined,
                uomId: this.isWarehouseItemLine(line) && line.tenantUomId != null
                    ? undefined : line.itemQuantity?.uom?.uomId,
                tenantUomId: this.isWarehouseItemLine(line) ? line.tenantUomId : undefined,
                totalWeight: this.cargoTotalWeights[index],
                weightUnitId: line.weightUnit?.weightId,
                length: line.itemLength,
                breadth: line.itemBreadth,
                height: line.itemHeight,
                lengthUnitId: line.lengthUnit?.lengthId,
                price: line.price,
                containerNumber: line.containerNumber?.trim() || undefined,
                isoEquipmentType: line.isoEquipmentType,
                sealNumber: line.sealNumber,
                bookingNumber: line.bookingNumber,
                billOfLadingNumber: line.billOfLadingNumber,
                houseBillNumber: line.houseBillNumber,
                customsDeclarationNumber: line.customsDeclarationNumber,
                journeyTemplateId: line.journeyTemplateId
            })),
            workforceRequirements: this.workforceRequirements
                .filter(row => !!row.fieldRoleId && Number(row.headcount ?? 0) > 0)
                .map(row => ({
                    fieldRoleId: row.fieldRoleId,
                    headcount: Number(row.headcount),
                    sellRate: row.sellRate ?? undefined,
                    billingUnit: row.sellRate == null ? undefined : row.billingUnit,
                    currencyCode: row.sellRate == null ? undefined : (row.currencyCode || this.currencyCode),
                    notes: row.notes?.trim() || undefined
                })),
            pricing: this.canViewShipmentPricing() ? {
                operationalCostCaptureEnabled: false,
                currencyCode: this.currencyCode,
                rateSource: this.rateSource,
                contractId: this.pricingContractId,
                contractRevisionId: this.pricingContractRevisionId,
                rateCardId: this.pricingRateCardId,
                buyingPrice: undefined,
                sellingPrice: this.shipment.sellingPrice,
                manualOverride: this.requiresManualOverrideReason(),
                overrideReason: this.manualOverrideReason,
                buyingPriceSource: undefined,
                sellingPriceSource: this.rateSource,
                resourceOwnershipType: undefined,
                resourceCompanyId: undefined,
                supplierCrmClientId: undefined,
                supplierCompanyId: undefined,
                supplierContractId: undefined,
                supplierContractRevisionId: undefined,
                supplierRateCardId: undefined,
                internalCostAmount: undefined,
                recommendedBuyingPrice: undefined,
                pricingWarning: undefined
            } : undefined,
            attachments: this.toIntakeAttachments()
        };
    }

    addWorkforceRequirement(): void {
        this.workforceRequirements = [...this.workforceRequirements, {
            headcount: 1,
            billingUnit: 'TASK',
            currencyCode: this.currencyCode
        }];
    }

    removeWorkforceRequirement(index: number): void {
        this.workforceRequirements = this.workforceRequirements.filter((_, rowIndex) => rowIndex !== index);
        this.refreshIntakePreviews();
    }

    workforceRequirementChanged(): void {
        this.refreshIntakePreviews();
    }

}
